<template>
  <section class="ai-assistant" :class="{ 'ai-assistant--busy': isBusy }">
    <div class="ai-assistant__hero tw-card">
      <div class="ai-assistant__header">
        <div class="hero-copy">
          <p class="ai-assistant__eyebrow">{{ heroEyebrow }}</p>
          <h2 class="ai-assistant__title">{{ heroTitle }}</h2>
          <p class="ai-assistant__lede">{{ heroLede }}</p>
        </div>
        <!-- KAN-94 (m6): no scope picker. Each user gets the widest scope the server offers
             (resolvePreferredScope): admins Company overview, managers My team, everyone else My work. -->
      </div>

      <div class="workspace-guided-actions">
        <div class="workspace-guided-actions__group">
          <p class="section-heading__eyebrow">Quick Start</p>
          <div class="workspace-action-grid workspace-action-grid--quick-start">
            <div
              v-for="action in managerQuickStartActions"
              :key="action.id"
              class="workspace-action-entry"
            >
              <button
                type="button"
                class="workspace-action-btn"
                :disabled="isBusy"
                @click="runWorkspaceAction(action)"
              >
                <span class="workspace-action-btn__label">
                  {{ action.label }}
                  <span v-if="!isLastWorkingDayAction(action)" class="beta-badge">Beta</span>
                </span>
                <span v-if="resolveWorkspaceActionHelperText(action)" class="workspace-action-btn__helper">
                  {{ resolveWorkspaceActionHelperText(action) }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="workspace-section tw-card tw-card--soft">
      <div class="workspace-section__header">
        <h3 class="workspace-section__title">Choose a Focus Area</h3>
      </div>

      <div class="workspace-area-grid">
        <button
          v-for="area in managerWorkspaceAreas"
          :key="area.id"
          type="button"
          class="workspace-area-card"
          :class="{ 'workspace-area-card--active': expandedWorkspaceArea === area.id }"
          :disabled="isBusy"
          @click="toggleWorkspaceArea(area.id)"
        >
          <div class="workspace-area-card__body">
            <div class="workspace-area-card__icon">{{ area.icon }}</div>
            <div>
              <h4 class="workspace-area-card__title">{{ area.label }}</h4>
              <p class="workspace-area-card__description">{{ area.description }}</p>
            </div>
          </div>
          <span class="workspace-area-card__chevron">&#8250;</span>
        </button>
      </div>

      <div v-if="activeWorkspaceArea" class="workspace-subprompt-panel">
        <div class="workspace-subprompt-panel__header">
          <p class="section-heading__eyebrow">{{ activeWorkspaceArea.label }}</p>
          <h4 class="workspace-subprompt-panel__title">What do you want to review?</h4>
          <p class="workspace-subprompt-panel__description">{{ activeWorkspaceArea.description }}</p>
        </div>

        <div class="workspace-subprompt-panel__group">
          <p class="workspace-subprompt-panel__group-label">Workflows</p>
        <div class="workspace-subprompt-grid">
          <div
            v-for="action in activeWorkspaceArea.actions"
            :key="action.id"
            class="workspace-action-entry"
          >
            <button
              type="button"
              class="workspace-subprompt-btn"
              :disabled="isBusy"
              @click="runWorkspaceAction(action)"
            >
              <span class="workspace-subprompt-btn__label">
                {{ action.label }}
                <span v-if="!isLastWorkingDayAction(action)" class="beta-badge">Beta</span>
              </span>
              <span v-if="resolveWorkspaceActionHelperText(action)" class="workspace-subprompt-btn__helper">
                {{ resolveWorkspaceActionHelperText(action) }}
              </span>
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>

    <div class="workspace-section tw-card tw-card--soft">
      <div class="workspace-section__header">
        <div>
          <h3 class="workspace-section__title">Ask Your Own Question</h3>
          <p class="workspace-section__copy">Ask about clients, tickets, team members, workload, risks, coaching, or opportunities.</p>
        </div>
        <button
          type="button"
          class="result-action-btn"
          :disabled="isBusy"
          @click="toggleHistoryPanel"
        >
          History
        </button>
      </div>

      <div v-if="isHistoryPanelOpen" class="conversation-history-panel">
        <div v-if="isHistoryLoading" class="tw-state">
          <p>Loading your conversations&hellip;</p>
        </div>
        <div v-else-if="historyError" class="tw-state tw-state--error">
          <p>{{ historyError }}</p>
        </div>
        <div v-else-if="!conversationHistory.length" class="tw-state">
          <p>You haven't started any conversations yet.</p>
        </div>
        <ul v-else class="conversation-history-list">
          <li
            v-for="conversation in conversationHistory"
            :key="conversation.id"
            class="conversation-history-item"
          >
            <button
              type="button"
              class="conversation-history-item__btn"
              :disabled="isBusy"
              @click="openConversationFromHistory(conversation)"
            >
              <span class="conversation-history-item__title">{{ conversationPreviewText(conversation) }}</span>
              <span class="conversation-history-item__meta">{{ conversationTimestampText(conversation) }}</span>
            </button>
          </li>
        </ul>
      </div>

      <div class="ai-assistant__composer ai-assistant__composer--workspace">
        <b-input
          v-model.trim="questionDraft"
          type="textarea"
          :placeholder="questionPlaceholder"
          :disabled="isBusy"
          rows="3"
        />
        <div class="composer-footer">
          <div class="composer-footer__meta">
            <span class="char-count">{{ questionDraft.length }} characters</span>
            <!-- KAN-116: superuser-only, also while impersonating (it follows the real login, so the
                 impersonated user never sees it). The server ignores rawMode from anyone else. -->
            <b-checkbox
              v-if="canUseRawMode"
              v-model="rawModeEnabled"
              class="raw-mode-toggle"
              data-testid="raw-mode-toggle"
              :disabled="isBusy"
            >
              Raw mode
              <span class="raw-mode-toggle__hint">(plain answer, no report format)</span>
            </b-checkbox>
          </div>
          <div class="composer-footer__actions">
            <!-- spec-ask-ux-impersonation-and-composer: starts a fresh thread (same scope);
                 saved History is untouched. -->
            <button
              type="button"
              class="link-btn composer-footer__new-conversation"
              data-testid="new-conversation"
              :disabled="isAsking || !hasConversationThread"
              @click="startNewConversation"
            >
              New conversation
            </button>
            <b-button
              type="is-primary"
              :loading="isAsking"
              :disabled="!questionDraft || isBusy"
              @click="submitDraftQuestion()"
            >
              Analyze
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="conversationMessages.length" ref="resultCard" class="ai-assistant__result tw-card">
      <div class="result-header">
        <div>
          <p class="result-header__eyebrow">Conversation</p>
          <h3 class="result-header__title">Ask Your Own Question</h3>
        </div>
      </div>

      <!-- KAN-88: newest turn first, directly under the composer. conversationMessages itself
           stays chronological (retry/replace/tests index it); only the rendering is reversed. -->
      <div
        v-for="turn in conversationTurns"
        :key="turn.id"
        class="thread-item"
        :class="{ 'thread-item--new': turn.id === highlightedTurnId }"
        :data-turn-id="turn.id"
      >
        <template v-for="message in turn.messages" :key="message.id">
          <div v-if="message.role === 'user'" class="question-bubble">
            <p class="bubble-label">You asked</p>
            <p>{{ message.displayQuestion || message.question }}</p>
          </div>

          <div v-else class="answer-card">
            <div v-if="message.loading" class="loading-state" aria-live="polite">
              <p class="loading-state__title">{{ loadingStatusTitle }}</p>
              <div class="loading-skeleton loading-skeleton--summary"></div>
              <div class="loading-skeleton loading-skeleton--line"></div>
              <div class="loading-skeleton loading-skeleton--line"></div>
              <div class="loading-skeleton loading-skeleton--line-short"></div>
            </div>

            <div v-else-if="message.error" class="tw-state tw-state--error">
              <p>{{ message.error }}</p>
              <b-button
                v-if="message.canRetry"
                type="is-light"
                size="is-small"
                :disabled="isBusy"
                @click="retryConversationMessage(message)"
              >
                Retry
              </b-button>
            </div>

            <template v-else>
              <RawAskView
                v-if="message.raw"
                :answer="message.answer"
              />

              <IntegrityBlockView
                v-else-if="messageHasIntegrityBlock(message)"
                :answer="message.answer"
                :limitations="message.limitations"
                :dataset-context-items="[]"
              />

              <AccessDeniedView
                v-else-if="message.accessDenied"
                :answer="message.answer"
              />

              <AskFallbackView
                v-else
                :show-banner="messageHasStructuredFallback(message)"
                :limitations="message.limitations"
                :sanitized-answer-html="messageSanitizedAnswerHtml(message)"
                :response-paragraphs="messageResponseParagraphs(message)"
              />
            </template>
          </div>
        </template>
      </div>
    </div>

    <div v-else-if="conversationNotFound" ref="resultCard" class="ai-assistant__result tw-card">
      <div class="tw-state tw-state--error">
        <p>This conversation could not be found.</p>
      </div>
    </div>

    <div v-else-if="conversationOpenError" ref="resultCard" class="ai-assistant__result tw-card">
      <div class="tw-state tw-state--error">
        <p>{{ conversationOpenError }}</p>
      </div>
    </div>

    <div v-else-if="latestResponse" ref="resultCard" class="ai-assistant__result tw-card">
      <div class="result-header">
        <div>
          <p class="result-header__eyebrow">{{ resultHeaderEyebrow }}</p>
          <h3 class="result-header__title">{{ resultHeaderTitle }}</h3>
        </div>
        <div class="result-header__right">
          <p v-if="resultHeaderMeta" class="result-header__meta">{{ resultHeaderMeta }}</p>
          <div v-if="canExportReport" class="result-header__actions">
            <button type="button" class="result-action-btn" @click="copyReport">
              Copy
            </button>
            <b-dropdown position="is-bottom-left" :mobile-modal="false">
              <template #trigger>
                <button type="button" class="result-action-btn">
                  Export &#9662;
                </button>
              </template>
              <b-dropdown-item aria-role="listitem" @click="exportReport('md')">
                Export as .md
              </b-dropdown-item>
              <b-dropdown-item aria-role="listitem" @click="exportReport('pdf')">
                Export as .pdf
              </b-dropdown-item>
              <template v-if="isQbrMode && isAdmin">
                <b-dropdown-item
                  aria-role="listitem"
                  :disabled="!qbrReportClientName || Boolean(qbrExportingFormat)"
                  @click="exportReport('pptx')"
                >
                  {{ qbrExportingFormat === 'pptx' ? 'Exporting…' : 'Export as .pptx' }}
                </b-dropdown-item>
                <b-dropdown-item
                  aria-role="listitem"
                  :disabled="!qbrReportClientName || Boolean(qbrExportingFormat)"
                  @click="exportReport('docx')"
                >
                  {{ qbrExportingFormat === 'docx' ? 'Exporting…' : 'Export as .docx' }}
                </b-dropdown-item>
              </template>
            </b-dropdown>
            <span v-if="qbrExportError" class="result-action-error">{{ qbrExportError }}</span>
          </div>
        </div>
      </div>

      <div v-if="!latestResponse" class="tw-state">
        <p>Choose a workspace action or ask your own question.</p>
      </div>

      <div v-else class="thread-item">
        <div class="answer-card">
          <div v-if="latestResponse.loading" class="loading-state" aria-live="polite">
            <p class="loading-state__title">{{ loadingStatusTitle }}</p>
            <p v-if="loadingStatusContext" class="loading-state__meta">{{ loadingStatusContext }}</p>
            <div class="loading-skeleton loading-skeleton--summary"></div>
            <div class="loading-skeleton loading-skeleton--line"></div>
            <div class="loading-skeleton loading-skeleton--line"></div>
            <div class="loading-skeleton loading-skeleton--line-short"></div>
          </div>

          <div v-else-if="latestResponse.error" class="tw-state tw-state--error">
            <p>{{ latestResponse.error }}</p>
            <b-button
              v-if="latestResponse.canRetry"
              type="is-light"
              size="is-small"
              :disabled="isBusy"
              @click="submitQuestion(latestResponse.rawQuestion || latestResponse.question, {
                displayQuestion: latestResponse.displayQuestion,
                dateRangePreset: latestResponse.dateRangePreset,
                outputMode: latestResponse.requestOutputMode,
                workspaceSource: latestResponse.requestWorkspaceSource,
                analysisContext: latestResponse.requestAnalysisContext,
              })"
            >
              Retry
            </b-button>
          </div>

          <template v-else>
            <RawAskView
              v-if="isRawMode"
              :answer="latestResponse.answer"
            />

            <IntegrityBlockView
              v-else-if="hasIntegrityBlock"
              :answer="latestResponse.answer"
              :limitations="latestResponse.limitations"
              :dataset-context-items="datasetContextItems"
            />

            <AccessDeniedView
              v-else-if="isAccessDenied"
              :answer="latestResponse.answer"
            />

            <AskFallbackView
              v-else-if="hasStructuredFallback"
              :limitations="latestResponse.limitations"
              :sanitized-answer-html="sanitizedAnswerHtml"
              :response-paragraphs="responseParagraphs"
            />

            <template v-else>
              <QbrBriefView
                v-if="isQbrMode"
                :report-view="latestReportView"
                :signal-strength="signalStrength"
                :sections="qbrSections"
                :ticket-refs="ticketRefs"
                @ticket-ref-clicked="openTicketDrawer"
              />

              <EvidenceReviewView
                v-else-if="isEvidenceReviewMode"
                :report-view="latestReportView"
                :signal-strength="signalStrength"
                :evidence-items="evidenceReviewItems"
                :followup-questions="evidenceReviewFollowupQuestions"
                :caveats="evidenceReviewCaveats"
              />

              <CoachingBriefView
                v-else-if="isCoachingBriefMode"
                :report-view="latestReportView"
                :signal-strength="signalStrength"
                :sections="coachingBriefSections"
                :ticket-refs="ticketRefs"
                @ticket-ref-clicked="openTicketDrawer"
              />

              <InsufficientSignalView
                v-else-if="isInsufficientSignalMode"
                :status="latestReportView && latestReportView.status"
                :cause="latestReportView && latestReportView.cause"
                :evidence-reviewed="latestReportView && latestReportView.evidenceReviewed"
                :suggested-actions="latestReportView && latestReportView.suggestedActions || []"
              />

              <OperationalInsightView
                v-else
                :report-view="latestReportView"
                :has-yesterday-report="hasYesterdayReport"
                :yesterday-report="operationalYesterdayReport"
                :has-weekly-report="hasWeeklyReport"
                :weekly-report="operationalWeeklyReport"
                :signal-strength="signalStrength"
                :ticket-refs="ticketRefs"
                @ticket-ref-clicked="openTicketDrawer"
              />
            </template>

            <TicketDetailDrawer
              :visible="drawerOpen"
              :ticket="drawerTicket || {}"
              @close="drawerOpen = false"
            />
          </template>
        </div>
      </div>
    </div>

    <b-modal
      v-model="isContextModalActive"
      has-modal-card
      :can-cancel="['escape', 'outside', 'button']"
      :width="860"
      trap-focus
    >
      <div class="modal-card workspace-modal-card">
        <header class="modal-card-head workspace-modal__header">
          <div>
            <p v-if="modalEyebrow" class="section-heading__eyebrow">{{ modalEyebrow }}</p>
            <h3 class="workspace-modal__title">Choose What to Analyze</h3>
            <p
              v-if="pendingActionLabel"
              class="workspace-modal__helper workspace-modal__helper--question"
            >{{ pendingActionLabel }}</p>
            <p v-if="pendingActionHelper" class="workspace-modal__helper workspace-modal__helper--secondary">{{ pendingActionHelper }}</p>
            <p v-if="modalIntroCopy" class="workspace-modal__helper workspace-modal__helper--secondary">{{ modalIntroCopy }}</p>
            <p v-if="modalRequirementHint" class="workspace-modal__helper workspace-modal__helper--secondary">{{ modalRequirementHint }}</p>
          </div>
        </header>

        <section class="modal-card-body workspace-modal__body">
          <div class="workspace-modal__grid">
            <div class="workspace-modal__field">
              <label class="workspace-modal__label">Date range</label>
              <div class="workspace-modal__date-grid">
                <b-datepicker
                  v-model="modalDateStart"
                  placeholder="Start date"
                  icon="calendar-today"
                  :max-date="modalDateEnd || null"
                  :disabled="isModalSubmitting"
                  append-to-body
                />
                <b-datepicker
                  v-model="modalDateEnd"
                  placeholder="End date"
                  icon="calendar-today"
                  :min-date="modalDateStart || null"
                  :disabled="isModalSubmitting"
                  append-to-body
                />
              </div>
              <p class="workspace-modal__hint">
                {{ modalAvailableDateRangeLabel }}
              </p>
            </div>

            <div v-if="!isQbrAction" class="workspace-modal__field">
              <div class="workspace-modal__label-row">
                <label class="workspace-modal__label">Team member</label>
                <button type="button" class="link-btn" :disabled="isModalSubmitting" @click="toggleAllModalTeamMembers">
                  {{ areAllTeamMembersSelected ? 'Clear all' : 'Select all' }}
                </button>
              </div>
              <div class="workspace-modal__picker">
                <label
                  v-for="option in modalTeamMemberOptions"
                  :key="`team-${option.value}`"
                  class="workspace-modal__check"
                >
                  <input
                    type="checkbox"
                    :checked="modalSelectedTeamMembers.includes(option.value)"
                    :disabled="isModalSubmitting"
                    @change="toggleModalTeamMember(option.value)"
                  >
                  <span>{{ option.label }}</span>
                </label>
              </div>
              <p class="workspace-modal__hint">{{ modalTeamMemberSummaryLabel }}</p>
            </div>

            <div class="workspace-modal__field">
              <div class="workspace-modal__label-row">
                <label class="workspace-modal__label">Client</label>
                <button v-if="!isQbrAction" type="button" class="link-btn" :disabled="isModalSubmitting" @click="toggleAllModalClients">
                  {{ areAllClientsSelected ? 'Clear all' : 'Select all' }}
                </button>
              </div>
              <template v-if="isQbrAction">
                <select
                  class="workspace-modal__client-select"
                  :value="modalSelectedClients[0] || ''"
                  :disabled="isModalSubmitting"
                  @change="handleQbrClientSelect($event.target.value)"
                >
                  <option value="">Select a client…</option>
                  <option
                    v-for="option in modalClientOptions"
                    :key="option.value"
                    :value="option.value"
                  >{{ option.label }}</option>
                </select>
              </template>
              <template v-else>
                <div class="workspace-modal__picker">
                  <label
                    v-for="option in modalClientOptions"
                    :key="`client-${option.value}`"
                    class="workspace-modal__check"
                  >
                    <input
                      type="checkbox"
                      :checked="modalSelectedClients.includes(option.value)"
                      :disabled="isModalSubmitting"
                      @change="toggleModalClient(option.value)"
                    >
                    <span>{{ option.label }}</span>
                  </label>
                </div>
                <p class="workspace-modal__hint">{{ modalClientSummaryLabel }}</p>
              </template>
            </div>

          </div>
        </section>

        <footer class="modal-card-foot workspace-modal__actions">
          <b-button type="is-text" :disabled="isModalSubmitting" @click="closeContextModal">
            Cancel
          </b-button>
          <b-button
            type="is-primary"
            :loading="isModalSubmitting"
            :disabled="!canSubmitContextModal"
            @click="submitContextModal"
          >
            {{ modalSubmitLabel }}
          </b-button>
        </footer>
      </div>
    </b-modal>

    <div v-if="isAsking" class="ai-assistant__overlay" aria-live="polite" aria-busy="true">
      <div class="overlay-card">
        <b-loading :is-full-page="false" :active="true" />
        <p class="overlay-title">{{ overlayTitle }}</p>
        <transition name="fade-slide" mode="out-in">
          <p :key="loadingStatusTitle" class="overlay-phrase">
            {{ loadingStatusTitle }}<span class="overlay-dots">{{ loadingDots }}</span>
          </p>
        </transition>
        <p v-if="loadingStatusContext" class="overlay-meta">{{ loadingStatusContext }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { loadVisitedTickets, markTicketVisited } from '@/utils/visitedTickets'
import AskFallbackView from '@/components/roster/ask/AskFallbackView.vue'
import RawAskView from '@/components/roster/ask/RawAskView.vue'
import IntegrityBlockView from '@/components/roster/ask/IntegrityBlockView.vue'
import AccessDeniedView from '@/components/roster/ask/AccessDeniedView.vue'
import InsufficientSignalView from '@/components/roster/ask/InsufficientSignalView.vue'
import OperationalInsightView from '@/components/roster/ask/OperationalInsightView.vue'
import QbrBriefView from '@/components/roster/ask/QbrBriefView.vue'
import EvidenceReviewView from '@/components/roster/ask/EvidenceReviewView.vue'
import CoachingBriefView from '@/components/roster/ask/CoachingBriefView.vue'
import TicketDetailDrawer from '@/components/roster/ask/TicketDetailDrawer.vue'
import {
  getWorkspaceConfig,
  resolveWorkspaceActionDisplayQuestion,
  resolveWorkspaceActionHelperText,
  resolveWorkspaceActionQuestion,
} from '@/components/roster/ask/insightWorkspaceRegistry.js'
import { formatHours, formatCount } from '@/utils/numberFormat.js'

const DEFAULT_SCOPE_LABELS = {
  self: 'My work',
  manager: 'My team',
  executive: 'Company overview',
}

const DEFAULT_DATE_RANGE_PRESETS = [
  { value: 'all_time', label: 'All time' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_14_days', label: 'Last 14 days' },
  { value: 'last_7_days', label: 'Last 7 days' },
]

const STRUCTURED_FALLBACK_MESSAGE = 'Structured JSON parsing failed; displaying raw model output instead.'

// KAN-88: how long a just-asked / just-answered turn stays highlighted.
const TURN_HIGHLIGHT_MS = 2000

// KAN-87: a follow-up sends the last few completed turns as priorTurns (the server keeps the
// same caps and prefers its own stored turns when the thread is saved). Impersonated threads
// are never saved (KAN-79), so for them this is the only memory a follow-up has.
const PRIOR_TURNS_LIMIT = 3
const PRIOR_TURN_QUESTION_CHARS = 300
const PRIOR_TURN_ANSWER_CHARS = 800
const PRIOR_TURN_TICKET_REFS = 20

function toPlainAnswerText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
}

// Ticket numbers an answer names — Autotask "T20261021.0159" and ConnectWise "#123456" or
// "ticket 123456" — from its text, plus citation refs that are a ticket number themselves
// (the same shapes the server accepts: a T-number or 5+ digits).
function extractAnswerTicketRefs(payload = {}) {
  const citationRefs = (Array.isArray(payload.citations) ? payload.citations : [])
    .map((citation) => String(citation?.ref || '').trim())
    .map((ref) => (/^#?\d{5,}$/.test(ref) ? `#${ref.replace(/^#/, '')}` : ref))
  const text = [payload.answer || '', payload.answerHtml || '', ...citationRefs].join(' ')
  const refs = [
    ...(text.match(/\bT\d{8}\.\d{3,4}\b/g) || []),
    ...Array.from(text.matchAll(/#(\d{5,})\b/g), (match) => match[1]),
    ...Array.from(text.matchAll(/\bticket\s+(?:number\s+|no\.?\s+)?#?(\d{5,})\b/gi), (match) => match[1]),
  ]
  return Array.from(new Set(refs)).slice(0, PRIOR_TURN_TICKET_REFS)
}

// KAN-95: the LWD/LWW Team section is model-written and names only a few people (KAN-91), so
// it stays hidden in the app (and in copy/export, which read the rendered report). The daily
// report shows the deterministic Team table (latestResponse.teamTable) in its place; the weekly
// has no table yet. Set to false to bring the model-written section back.
const HIDE_REPORT_TEAM_SECTION = true

// QBR_ACTION_IDS marks which quick-start actions use the single-client + date-range
// modal layout (vs. the multi-client/team picker shown for other actions).
const QBR_ACTION_IDS = ['prepare-qbr']

// Metric rows whose values are hours, matched by label — mirrors the backend's
// qbrReportSections.js HOURS_METRIC_LABELS so already-saved reports render correctly too.
const QBR_HOURS_METRIC_LABELS = new Set(['Hours logged'])

export default {
  name: 'AIAssistant',
  components: {
    AskFallbackView,
    RawAskView,
    IntegrityBlockView,
    AccessDeniedView,
    InsufficientSignalView,
    OperationalInsightView,
    QbrBriefView,
    EvidenceReviewView,
    CoachingBriefView,
    TicketDetailDrawer,
  },
  props: {
    isManager: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    selectedClientName: {
      type: String,
      default: '',
    },
    selectedTeamMemberName: {
      type: String,
      default: '',
    },
    autoRunActionId: {
      type: String,
      default: '',
    },
    initialTicketNbr: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedScope: this.isManager ? 'manager' : 'self',
      hasAutoRunAction: false,
      initialTicketDrawerOpened: false,
      expandedWorkspaceArea: '',
      questionDraft: '',
      // KAN-116: superuser raw mode for Ask conversation turns.
      rawModeEnabled: false,
      selectedPromptText: '',
      selectedPromptOutputMode: '',
      isAsking: false,
      isMetadataLoading: false,
      latestResponse: null,
      conversationId: null,
      conversationMessages: [],
      // KAN-88: id of the turn briefly highlighted after it is asked / answered.
      highlightedTurnId: '',
      conversationNotFound: false,
      conversationOpenError: '',
      conversationHistory: [],
      isHistoryLoading: false,
      isHistoryPanelOpen: false,
      historyError: '',
      askMetadata: null,
      rosterMembers: [],
      isRosterLoading: false,
      askStreamController: null,
      ticketRefs: [],
      qbrExportingFormat: null,
      qbrExportError: '',
      drawerOpen: false,
      drawerTicket: null,
      streamingStatusMessage: '',
      streamingContextSummary: '',
      streamingContextMeta: {},
      selectedDateRangePreset: 'all_time',
      localSelectedClientName: String(this.selectedClientName || '').trim(),
      localSelectedTeamMemberName:
        String(this.selectedTeamMemberName || '').trim() ||
        `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim(),
      isContextModalActive: false,
      isModalSubmitting: false,
      pendingAction: null,
      pendingQuestionText: '',
      pendingDisplayQuestion: '',
      pendingOutputMode: 'auto',
      pendingDateRangePreset: 'all_time',
      pendingWorkspaceSource: null,
      pendingContextReason: '',
      modalDateStart: null,
      modalDateEnd: null,
      modalSelectedClients: [],
      modalSelectedTeamMembers: [],
      loadingPhraseIndex: 0,
      loadingPhraseIntervalId: null,
      ellipsisIntervalId: null,
      ellipsisCount: 1,
      loadingPhrasesByScope: {
        manager: [
          'Analyzing your question',
          'Reviewing workload patterns and related signals',
          'Collecting key insights',
          'Identifying patterns across your work system',
          'Preparing a clear summary of what matters most',
        ],
        self: [
          'Analyzing your question',
          'Reviewing workload patterns and related signals',
          'Collecting key insights',
          'Identifying patterns across your work system',
          'Preparing a clear summary of what matters most',
        ],
        executive: [
          'Analyzing your question',
          'Reviewing workload patterns and related signals',
          'Collecting key insights',
          'Identifying patterns across your work system',
          'Preparing a clear summary of what matters most',
        ],
      },
      scopeContentByType: {
        self: {
          heroTitle: 'Ask about workload, delivery risk, coaching, and growth.',
          heroLede: 'Teamwrkr is built to answer operating questions from your work data. Start with the current context, then move into a focused question.',
          overlayTitle: 'Preparing your insights',
          placeholder: 'Ask about workload, delivery risk, client impact, or team readiness',
          viewLabel: 'Individual workspace',
        },
        manager: {
          heroTitle: 'Understand what is happening across your team, clients, and work.',
          heroLede: 'Use your work data to answer practical operating questions and make confident decisions.',
          overlayTitle: 'Preparing your insights',
          placeholder: 'Ask about workload, clients, delivery risk, opportunities, or team readiness',
          viewLabel: 'Manager and team view',
        },
        executive: {
          heroTitle: 'Ask about company capacity, delivery exposure, and executive planning.',
          heroLede: 'Teamwrkr is built to answer operating questions from your work data. Start with the current context, then move into a focused question.',
          overlayTitle: 'Preparing your insights',
          placeholder: 'Ask about workload, delivery risk, client impact, or team readiness',
          viewLabel: 'Executive leadership view',
        },
      },
    }
  },
  computed: {
    ...mapState(useUserStore, ['lastRefreshed', 'user', 'isImpersonating', 'impersonatorIsSuperUser']),
    // KAN-88: pairs each user message with the assistant message that follows it, newest turn
    // first. conversationMessages stays chronological — retry, replace and the tests index it.
    hasConversationThread() {
      return this.conversationMessages.length > 0 || Boolean(this.conversationId)
        || this.conversationNotFound || Boolean(this.conversationOpenError)
    },
    conversationTurns() {
      const turns = []
      this.conversationMessages.forEach((message, index) => {
        const current = turns[turns.length - 1]
        if (message.role !== 'user' && current && !current.hasAnswer) {
          current.messages.push(message)
          current.hasAnswer = true
          return
        }
        // Index fallback keeps keys unique (and the turn scrollable) if a row ever lacks an id.
        turns.push({ id: message.id || `turn-${index}`, messages: [message], hasAnswer: message.role !== 'user' })
      })
      return turns.reverse()
    },
    // spec-reporting-hierarchy: "My team" is for people with reports (isManager is derived from
    // the reporting tree server-side). Being an admin is a separate permission — an admin with
    // no reports gets Company overview from the server's availableScopes, not manager scope
    // (which the server would 403).
    canUseManagerScope() {
      return this.isManager === true
    },
    defaultScope() {
      return this.isManager ? 'manager' : 'self'
    },
    fallbackScopeOptions() {
      const scopes = [{ type: 'self', label: DEFAULT_SCOPE_LABELS.self }]

      if (this.canUseManagerScope) {
        scopes.unshift({ type: 'manager', label: DEFAULT_SCOPE_LABELS.manager })
      }

      return scopes
    },
    availableScopeOptions() {
      const metadataScopes = Array.isArray(this.askMetadata?.availableScopes)
        ? this.askMetadata.availableScopes
            .map((scope) => {
              const type = String(scope?.type || '').trim()
              if (!type) return null

              return {
                type,
                label: this.scopeLabelFor(type, String(scope?.label || '').trim()),
              }
            })
            .filter(Boolean)
        : []

      return metadataScopes.length ? metadataScopes : this.fallbackScopeOptions
    },
    availableScopeTypes() {
      return this.availableScopeOptions.map((scope) => scope.type)
    },
    availableDateRangePresets() {
      const metadataPresets = Array.isArray(this.askMetadata?.availableDateRangePresets)
        ? this.askMetadata.availableDateRangePresets
            .map((preset) => {
              const value = String(preset?.value || '').trim()
              if (!value) return null
              if (value === 'last_1_day') return null

              const defaultPreset = DEFAULT_DATE_RANGE_PRESETS.find((candidate) => candidate.value === value)

              return {
                value,
                label: String(preset?.label || '').trim() || defaultPreset?.label || value,
              }
            })
            .filter(Boolean)
        : []

      if (!metadataPresets.length) {
        return DEFAULT_DATE_RANGE_PRESETS
      }

      const mergedPresets = [...metadataPresets]
      DEFAULT_DATE_RANGE_PRESETS.forEach((preset) => {
        if (!mergedPresets.some((candidate) => candidate.value === preset.value)) {
          mergedPresets.push(preset)
        }
      })

      return mergedPresets
    },
    activeScopeContent() {
      return this.scopeContentByType[this.selectedScope] || this.scopeContentByType.self
    },
    heroEyebrow() {
      return this.activeScopeContent.viewLabel || 'AI assistant'
    },
    heroTitle() {
      return this.activeScopeContent.heroTitle
    },
    heroLede() {
      return this.activeScopeContent.heroLede
    },
    activeLoadingPhrases() {
      return this.loadingPhrasesByScope[this.selectedScope] || ['Preparing insights']
    },
    currentLoadingPhraseBase() {
      return this.activeLoadingPhrases[this.loadingPhraseIndex] || 'Preparing insights'
    },
    loadingStatusTitle() {
      return this.streamingStatusMessage || this.currentLoadingPhraseBase
    },
    loadingStatusContext() {
      return [this.streamingDateRangeLabel, this.streamingSignalLabel, this.streamingContextSummary].filter(Boolean).join(' | ')
    },
    loadingDots() {
      return '.'.repeat(this.ellipsisCount)
    },
    isBusy() {
      return this.isAsking || this.isMetadataLoading
    },
    overlayTitle() {
      return this.activeScopeContent.overlayTitle
    },
    questionPlaceholder() {
      return this.activeScopeContent.placeholder
    },
    workspaceConfig() {
      return getWorkspaceConfig(this.selectedScope)
    },
    managerQuickStartActions() {
      return this.workspaceConfig.quickStartActions || []
    },
    managerWorkspaceAreas() {
      return this.workspaceConfig.workspaceAreas || []
    },
    clientOptions() {
      const options = Array.isArray(this.askMetadata?.clientOptions) ? this.askMetadata.clientOptions : []
      // Deliberately no ticket-count suffix — the count is scoped to a different
      // window (all-time) than whatever date range the modal has selected, and
      // showing two different ticket counts side by side read as a bug.
      const normalized = options
        .map((option) => {
          const value = String(option?.value || option?.label || '').trim()
          return value ? { value, label: value } : null
        })
        .filter(Boolean)

      if (
        this.localSelectedClientName &&
        !normalized.some((option) => option.value === this.localSelectedClientName)
      ) {
        normalized.unshift({
          value: this.localSelectedClientName,
          label: this.localSelectedClientName,
        })
      }

      return normalized
    },
    teamMemberOptions() {
      if (this.selectedScope === 'self') {
        const selfOption = this.normalizeTeamMemberOption(this.user)
        return selfOption ? [selfOption] : []
      }

      const entries = [
        ...(Array.isArray(this.rosterMembers) ? this.rosterMembers : []),
        ...(Array.isArray(this.user?.roster) ? this.user.roster : []),
      ]
      const seen = new Set()
      const options = []

      entries.forEach((entry) => {
        const option = this.normalizeTeamMemberOption(entry)
        if (!option || seen.has(option.value)) return
        seen.add(option.value)
        options.push(option)
      })

      options.sort((left, right) => left.label.localeCompare(right.label))

      if (
        this.localSelectedTeamMemberName &&
        !options.some((option) => option.value === this.localSelectedTeamMemberName)
      ) {
        options.unshift({
          value: this.localSelectedTeamMemberName,
          label: this.localSelectedTeamMemberName,
        })
      }

      return options
    },
    modalClientOptions() {
      return this.clientOptions.map((option) => ({
        value: option.value,
        label: option.label || option.value,
      }))
    },
    modalTeamMemberOptions() {
      return this.teamMemberOptions.map((option) => ({
        value: option.value,
        label: option.label || option.value,
      }))
    },
    areAllClientsSelected() {
      return this.modalClientOptions.length > 0
        && this.modalSelectedClients.length === this.modalClientOptions.length
    },
    areAllTeamMembersSelected() {
      // spec-ask-selection-label-accuracy.md (review patch): compare membership, not just count.
      // A stale selection carried over from another scope makes the counts match while the
      // selection actually holds a different person, so a length check would report
      // "All team members included" for a set that excludes the only listed member.
      return this.modalTeamMemberOptions.length > 0
        && this.modalTeamMemberOptions.every((option) => this.modalSelectedTeamMembers.includes(option.value))
    },
    modalClientSummaryLabel() {
      if (!this.modalSelectedClients.length || this.areAllClientsSelected) {
        return 'All clients included'
      }
      return this.describeModalSelections(this.modalSelectedClients, this.modalClientOptions)
    },
    modalTeamMemberSummaryLabel() {
      if (!this.modalSelectedTeamMembers.length || this.areAllTeamMembersSelected) {
        return 'All team members included'
      }
      return this.describeModalSelections(this.modalSelectedTeamMembers, this.modalTeamMemberOptions)
    },
    modalAvailableDateRangeLabel() {
      const start = this.formatDateOnly(this.askMetadata?.dateRange?.start)
      const end = this.formatDateOnly(this.askMetadata?.dateRange?.end)
      if (start && end) return `Data available from ${start} to ${end}`
      return 'No valid activity date available'
    },
    pendingActionLabel() {
      return this.pendingDisplayQuestion || this.pendingAction?.label || ''
    },
    pendingActionHelper() {
      return this.resolveWorkspaceActionHelperText(this.pendingAction)
    },
    pendingActionContextRequirements() {
      return this.pendingAction?.contextRequirements || {}
    },
    isQbrAction() {
      return QBR_ACTION_IDS.includes(this.pendingAction?.id)
    },
    modalEyebrow() {
      if (this.pendingContextReason === 'freeform_dynamic') return 'Refine your question'
      return ''
    },
    modalIntroCopy() {
      if (this.isQbrAction) return ''
      if (this.pendingContextReason === 'action_required') {
        return 'This action needs context before it runs. Confirm the date range, clients, and team members to analyze.'
      }
      if (this.pendingContextReason === 'freeform_dynamic') {
        return 'Your question appears to depend on scope. Confirm the date range, clients, and team members before running the report.'
      }
      if (this.pendingContextReason === 'freeform_explicit') {
        return 'Choose the data slice you want reviewed before running your question.'
      }
      if (this.pendingAction?.launchBehavior === 'defaults_then_optional_context') {
        return 'This action can run with defaults, but you chose to refine the analysis context first.'
      }
      return 'Confirm the data slice that should be used for this analysis.'
    },
    modalRequirementHint() {
      if (this.isQbrAction) return ''
      const req = this.pendingActionContextRequirements
      const requiresSingleClient = req.requiresSingleClient || req.requiresClient
      const requiresDateRange = req.requiresDateRange
      if (!requiresSingleClient && !requiresDateRange) return ''
      if (requiresSingleClient && requiresDateRange) return 'Select one client and a date range to run this report.'
      if (requiresSingleClient) return 'Select one client to run this report.'
      return 'Select a date range to run this report.'
    },
    modalSubmitLabel() {
      if (this.pendingContextReason === 'action_required') return 'Run analysis'
      if (this.pendingContextReason === 'freeform_dynamic') return 'Analyze question'
      if (this.pendingContextReason === 'freeform_explicit') return 'Analyze with this context'
      if (this.pendingAction?.launchBehavior === 'defaults_then_optional_context') {
        return 'Analyze with this context'
      }
      return 'Analyze'
    },
    canSubmitContextModal() {
      const requiresClient = this.pendingActionContextRequirements.requiresClient === true
      const requiresSingleClient = this.pendingActionContextRequirements.requiresSingleClient === true
      const requiresTeamMember = this.pendingActionContextRequirements.requiresTeamMember === true
      const requiresSingleTeamMember = this.pendingActionContextRequirements.requiresSingleTeamMember === true
      const requiresDateRange = this.pendingActionContextRequirements.requiresDateRange === true
      const hasDateRange = !requiresDateRange || Boolean(this.modalDateStart && this.modalDateEnd)
      const hasClient = !requiresClient || this.modalSelectedClients.length > 0
      const hasTeamMember = !requiresTeamMember || this.modalSelectedTeamMembers.length > 0
      const hasRequiredClientCount = !requiresSingleClient || this.modalSelectedClients.length === 1
      const hasRequiredTeamMemberCount = !requiresSingleTeamMember || this.modalSelectedTeamMembers.length === 1

      return Boolean(
        hasDateRange &&
        hasClient &&
        hasTeamMember &&
        hasRequiredClientCount &&
        hasRequiredTeamMemberCount &&
        this.pendingQuestionText &&
        !this.isBusy,
      )
    },
    effectiveSelectedClientName() {
      return String(this.localSelectedClientName || this.selectedClientName || '').trim()
    },
    // Bug (2026-10-13, dev): the QBR export buttons stayed disabled forever even after the
    // brief fully rendered. Root cause: QbrBriefView's clientName prop was bound to
    // effectiveSelectedClientName, which reflects LIVE modal/session selection state —
    // submitContextModal() calls resetModalSelections() right after submitting (clearing
    // localSelectedClientName back to ''), while the rendered report itself (latestResponse)
    // persists on screen independently. By the time a user looks at the finished report, the
    // live selection has already been reset, so effectiveSelectedClientName is empty for a
    // report that's clearly scoped to a real client. requestWorkspaceSource/
    // requestAnalysisContext are captured on latestResponse AT SUBMIT TIME (see submitQuestion)
    // and never reset, so they reliably name the client the currently-displayed report is for,
    // independent of whatever the modal's live state has moved on to since.
    qbrReportClientName() {
      return String(
        this.latestResponse?.requestWorkspaceSource?.selectedClientName ||
        this.latestResponse?.requestAnalysisContext?.clientSelection?.clientNames?.[0] ||
        ''
      ).trim()
    },
    // Bug (2026-10-13, Phil): the export buttons' "latest" lookup picks whichever QBR report was
    // generated most recently FOR THIS CLIENT, with no regard to which quarter is actually on
    // screen — so regenerating Q3 after Q2 for the same client, then going back to export Q2,
    // silently served Q3's file instead. The request's own exact date range (captured on
    // latestResponse the same way qbrReportClientName is, at submit time, and never reset) lets
    // the backend resolve the EXACT report this specific response is for instead of guessing by
    // recency — see qbrReport.service.js's getQbrReport.
    qbrReportDateRange() {
      const dateRange = this.latestResponse?.requestAnalysisContext?.dateRange || {}
      return {
        startDate: dateRange.startDate || '',
        endDate: dateRange.endDate || '',
      }
    },
    effectiveSelectedTeamMemberName() {
      return String(
        this.localSelectedTeamMemberName ||
        this.selectedTeamMemberName ||
        (this.selectedScope === 'self'
          ? `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim()
          : ''),
      ).trim()
    },
    activeScopeLabel() {
      return this.scopeLabelFor(this.selectedScope, '')
    },
    activeClientContextLabel() {
      return this.effectiveSelectedClientName || 'All clients'
    },
    activeTeamMemberContextLabel() {
      if (this.selectedScope === 'self') {
        return this.effectiveSelectedTeamMemberName || 'Current user'
      }
      return this.effectiveSelectedTeamMemberName || 'All team members'
    },
    currentContextHeadline() {
      return [
        this.activeClientContextLabel,
        this.activeTeamMemberContextLabel,
        this.currentDateRangeContextLabel,
      ].filter(Boolean).join(' · ')
    },
    currentDateRangeContextLabel() {
      return this.headerDateRangeDisplay || 'Current date range'
    },
    activeSelectionSummary() {
      const parts = [
        `Scope: ${this.activeScopeLabel}`,
        `Client: ${this.activeClientContextLabel}`,
        `Team member: ${this.activeTeamMemberContextLabel}`,
        `Date range: ${this.currentDateRangeContextLabel}`,
        `Source: ${this.sourceDataLabel}`,
      ]
      return parts.join(' | ')
    },
    activeWorkspaceArea() {
      return this.managerWorkspaceAreas.find((area) => area.id === this.expandedWorkspaceArea) || null
    },
    latestMeta() {
      return this.normalizeMeta(this.latestResponse || {})
    },
    sourceDataLabel() {
      return this.formatDataSource(this.pickFirst(
        this.latestMeta.sourceLabel,
        this.latestMeta.source,
        this.latestMeta.integrationName,
        this.latestMeta.integration,
      )) || 'ConnectWise PSA data'
    },
    headerDateRangeDisplay() {
      const presetRange = this.formatPresetDateRange(this.selectedDateRangePreset)
      if (presetRange) return presetRange

      return this.formatDateRange(this.latestMeta) || 'Earliest available activity to latest available activity'
    },
    streamingDateRangeLabel() {
      return this.formatDateRange(this.streamingContextMeta)
    },
    streamingSignalLabel() {
      const strength = this.streamingContextMeta?.signalStrength?.strength
      if (strength === 'HIGH') return 'Strong signal'
      if (strength === 'MODERATE') return 'Medium signal'
      if (strength === 'INSUFFICIENT') return 'Insufficient signal'
      if (strength === 'LOW') return 'Weak signal'
      return ''
    },
    responseParagraphs() {
      const answer = String(this.responseTextContent || '').trim()
      if (!answer) return []

      return answer
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
    },
    responseMode() {
      const explicitMode = this.normalizeOutputMode(this.latestResponse?.outputMode)
      if (explicitMode) return explicitMode
      if (this.latestResponse?.qbrBrief) return 'qbr_brief'
      if (this.latestResponse?.evidenceReview) return 'evidence_review'
      if (this.latestResponse?.coachingBrief) return 'coaching_brief'
      return 'operational_insight'
    },
    isOperationalMode() {
      return this.responseMode === 'operational_insight'
    },
    isQbrMode() {
      return this.responseMode === 'qbr_brief'
    },
    isEvidenceReviewMode() {
      return this.responseMode === 'evidence_review'
    },
    isCoachingBriefMode() {
      return this.responseMode === 'coaching_brief'
    },
    isInsufficientSignalMode() {
      return this.responseMode === 'insufficient_signal'
    },
    hasStructuredFallback() {
      return Array.isArray(this.latestResponse?.limitations)
        && this.latestResponse.limitations.includes(STRUCTURED_FALLBACK_MESSAGE)
    },
    isRawMode() {
      return this.latestResponse?.raw === true
    },
    // KAN-116: a superuser, or a superuser impersonating someone (the real login decides).
    canUseRawMode() {
      return this.user?.isSuperUser === true || (this.isImpersonating && this.impersonatorIsSuperUser === true)
    },
    hasIntegrityBlock() {
      return this.latestResponse?.integrityCheck?.status === 'blocked'
        || this.latestResponse?.responsePolicy?.shouldBlock === true
    },
    isAccessDenied() {
      return this.latestResponse?.accessDenied === true
    },
    signalStrength() {
      return this.latestResponse?.signalStrength || null
    },
    responsePolicy() {
      return this.latestResponse?.responsePolicy || null
    },
    integrityCheck() {
      return this.latestResponse?.integrityCheck || null
    },
    evidenceProfile() {
      return this.latestResponse?.evidenceProfile || null
    },
    yesterdayReport() {
      return this.latestResponse?.yesterdayReport || null
    },
    hasYesterdayReport() {
      if (!this.isOperationalMode || this.hasStructuredFallback) return false
      if (this.latestResponse?.dateRangePreset !== 'last_1_day') return false

      return Boolean(
        this.yesterdayReportSummary.length ||
        this.yesterdayReportClients.length ||
        this.yesterdayReportTeam.length ||
        this.yesterdayReportFollowUps.length ||
        this.yesterdayReportDataSources.length ||
        this.latestResponse?.answer,
      )
    },
    yesterdayReportSummary() {
      const val = this.yesterdayReport?.summary
      if (typeof val === 'string' && val.trim()) return [val.trim()]
      const fromList = this.normalizeListContent(val)
      if (fromList.length) return fromList
      const answer = this.latestResponse?.answer
      return typeof answer === 'string' && answer.trim() ? [answer.trim()] : []
    },
    yesterdayReportClients() {
      return this.normalizeListContent(this.yesterdayReport?.clients)
    },
    yesterdayReportTeam() {
      return this.normalizeListContent(this.yesterdayReport?.team)
    },
    yesterdayReportFollowUps() {
      return this.normalizeListContent(this.yesterdayReport?.followUps)
    },
    yesterdayReportDataSources() {
      return this.normalizeListContent(this.yesterdayReport?.dataSources)
    },
    yesterdayReportTools() {
      return this.buildToolActivityItems(this.latestResponse?.toolActivity, this.yesterdayReport?.toolsSummary)
    },
    yesterdayReportSubtitle() {
      return this.datasetContextItems.map((item) => item.value).filter(Boolean).join(' | ')
    },
    dataSourcesContextItems() {
      const relevant = ['Source', 'Last synced', 'Analyzed date range']
      return this.datasetContextItems.filter((item) => relevant.includes(item.label))
    },
    // KAN-95: the daily report's code-computed Team table, already filtered to this viewer by the
    // backend. Rendered by YesterdayReportView (TeamTableView) in the hidden Team section's slot.
    yesterdayTeamTableSection() {
      const teamTable = this.latestResponse?.teamTable
      if (!teamTable || !Array.isArray(teamTable.rows)) return null
      return { title: 'Team', type: 'team_table', teamTable }
    },
    // KAN-103: the LWD v2 layout. No summary; What to watch and follow-ups, Clients, Team (people
    // with activity, hours to 2 decimals), Tools and About, all open.
    lwdV2Report() {
      const v2 = this.latestResponse?.lwdV2
      if (!v2) return null
      const list = (value) => (Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()) : [])
      const clients = v2.clients || {}
      // Every Follow-up and Watch item past the top ones is listed too, behind "Show all" (KAN-103).
      // The Clients lists stop at their top items, with no count (Phil, Oct 28: the rest is
      // activity data, and the Team and Tools tables have the totals), even on older reports
      // that still carry the rest rows.
      const clientGroups = [
        { heading: 'People-owned work', items: list(clients.peopleOwned) },
        { heading: 'Tool-generated', items: list(clients.toolGenerated) },
      ].filter((group) => group.items.length)
      const tools = v2.tools || {}
      const toolRows = Array.isArray(tools.rows) ? tools.rows : []
      const about = v2.about || {}
      const aboutItems = [
        about.source && `Source: ${about.source}`,
        about.dayCovered && `Day covered: ${about.dayCovered}.`,
        about.lastSync && `Last sync: ${about.lastSync}.`,
      ].filter(Boolean)
      const teamTable = this.latestResponse?.teamTable
      return {
        subtitle: v2.title || '',
        sections: [
          {
            title: 'What to watch and follow-ups',
            type: 'groups',
            expanded: true,
            lead: v2.cutoffLine || '',
            groups: [
              { heading: 'Follow-ups', items: list(v2.followUps), more: v2.followUpsMore || '', empty: v2.emptyFollowUps || '', rest: list(v2.followUpsRest), restNoun: 'follow-ups' },
              {
                heading: 'Watch',
                items: list(v2.watch),
                more: v2.watchMore || '',
                empty: v2.emptyWatch || '',
                rest: list(v2.watchRest),
                restNoun: 'watch items',
                note: typeof v2.waitingOnClientLine === 'string' ? v2.waitingOnClientLine : '',
              },
            ],
          },
          clientGroups.length ? { title: 'Clients', type: 'groups', expanded: true, groups: clientGroups } : null,
          teamTable && Array.isArray(teamTable.rows)
            ? { title: 'Team', type: 'team_table', teamTable, hideEmptyRows: true, hoursDecimals: 2 }
            : null,
          toolRows.length
            ? {
                title: 'Tools',
                type: 'table',
                expanded: true,
                columns: ['Tool', 'Tickets', 'Still New', 'Closed', 'High/Critical'],
                rows: [
                  ...toolRows.map((r) => ({ cells: [r.tool, r.tickets, r.stillNew, r.closed, r.highOrCritical] })),
                  ...(tools.total ? [{ total: true, cells: ['Total', tools.total.tickets, tools.total.stillNew, tools.total.closed, tools.total.highOrCritical] }] : []),
                ],
                note: v2.toolsNote || '',
              }
            : null,
          aboutItems.length ? { title: 'About this report', type: 'list', expanded: true, items: aboutItems } : null,
        ].filter(Boolean),
      }
    },
    operationalYesterdayReport() {
      if (this.lwdV2Report) return this.lwdV2Report
      const dataSourcesItems = this.yesterdayReportDataSources
      const dataContextItems = this.dataSourcesContextItems
      const hasDataSources = dataSourcesItems.length || dataContextItems.length

      const dataSourcesSection = hasDataSources
        ? Object.assign(
            this.buildYesterdayReportSection('Data Sources & Limits', dataSourcesItems) || {
              title: 'Data Sources & Limits',
              type: 'list',
              text: '',
              items: [],
            },
            dataContextItems.length ? { dataContextItems } : {},
          )
        : null

      return {
        subtitle: this.yesterdayReportSubtitle,
        sections: [
          this.buildYesterdayReportSection('Summary', this.yesterdayReportSummary),
          this.buildYesterdayReportSection('Clients', this.yesterdayReportClients),
          this.yesterdayTeamTableSection,
          HIDE_REPORT_TEAM_SECTION ? null : this.buildYesterdayReportSection('Team', this.yesterdayReportTeam),
          this.buildYesterdayReportSection('What your tools did', this.yesterdayReportTools),
          this.buildYesterdayReportSection('Follow-ups & Notes', this.yesterdayReportFollowUps),
          dataSourcesSection,
        ].filter(Boolean),
      }
    },
    weeklyReport() {
      return this.latestResponse?.weeklyReport || null
    },
    hasWeeklyReport() {
      if (!this.isOperationalMode || this.hasStructuredFallback) return false
      if (this.latestResponse?.dateRangePreset !== 'last_1_week') return false

      return Boolean(
        this.weeklyReportSummary.length ||
        this.weeklyReportClients.length ||
        this.weeklyReportTeam.length ||
        this.weeklyReportFollowUps.length ||
        this.weeklyReportDataSources.length ||
        this.latestResponse?.answer,
      )
    },
    weeklyReportSummary() {
      const val = this.weeklyReport?.summary
      if (typeof val === 'string' && val.trim()) return [val.trim()]
      const fromList = this.normalizeListContent(val)
      if (fromList.length) return fromList
      const answer = this.latestResponse?.answer
      return typeof answer === 'string' && answer.trim() ? [answer.trim()] : []
    },
    weeklyReportClients() {
      return this.normalizeListContent(this.weeklyReport?.clients)
    },
    weeklyReportTeam() {
      return this.normalizeListContent(this.weeklyReport?.team)
    },
    weeklyReportFollowUps() {
      return this.normalizeListContent(this.weeklyReport?.followUps)
    },
    weeklyReportDataSources() {
      return this.normalizeListContent(this.weeklyReport?.dataSources)
    },
    weeklyReportTools() {
      return this.buildToolActivityItems(this.latestResponse?.toolActivity, this.weeklyReport?.toolsSummary)
    },
    weeklyDepartedCleanup() {
      const items = this.latestResponse?.departedCleanup?.items
      return Array.isArray(items) ? items.filter((item) => typeof item === 'string' && item.trim()) : []
    },
    weeklyGoneQuietCleanup() {
      const items = this.latestResponse?.goneQuietCleanup?.items
      return Array.isArray(items) ? items.filter((item) => typeof item === 'string' && item.trim()) : []
    },
    // KAN-111: one group per owner (10 quietest tickets, the rest behind Show all), when the
    // payload has them; older payloads have only the per-owner lines.
    weeklyGoneQuietGroups() {
      const groups = this.latestResponse?.goneQuietCleanup?.groups
      if (!Array.isArray(groups)) return []
      const lines = (list) => (Array.isArray(list) ? list.filter((item) => typeof item === 'string' && item.trim()) : [])
      return groups
        .filter((group) => group && typeof group.heading === 'string' && lines(group.items).length)
        .map((group) => ({ heading: group.heading, items: lines(group.items), rest: lines(group.rest), restNoun: 'tickets' }))
    },
    weeklyReportSubtitle() {
      return this.datasetContextItems.map((item) => item.value).filter(Boolean).join(' | ')
    },
    operationalWeeklyReport() {
      const dataSourcesItems = this.weeklyReportDataSources
      const dataContextItems = this.dataSourcesContextItems
      const hasDataSources = dataSourcesItems.length || dataContextItems.length

      const dataSourcesSection = hasDataSources
        ? Object.assign(
            this.buildYesterdayReportSection('Data Sources & Limits', dataSourcesItems) || {
              title: 'Data Sources & Limits',
              type: 'list',
              text: '',
              items: [],
            },
            dataContextItems.length ? { dataContextItems } : {},
          )
        : null

      return {
        subtitle: this.weeklyReportSubtitle,
        sections: [
          this.buildYesterdayReportSection('Summary', this.weeklyReportSummary),
          this.buildYesterdayReportSection('Clients', this.weeklyReportClients),
          HIDE_REPORT_TEAM_SECTION ? null : this.buildYesterdayReportSection('Team', this.weeklyReportTeam),
          this.buildYesterdayReportSection('What your tools did', this.weeklyReportTools),
          this.buildYesterdayReportSection('Follow-ups & Notes', this.weeklyReportFollowUps),
          // Always a list: one line per departed owner, even when there is only one.
          this.weeklyDepartedCleanup.length
            ? { title: 'Departed staff cleanup', type: 'list', text: '', items: this.weeklyDepartedCleanup }
            : null,
          // KAN-111: one line per owner of tickets past the gone-quiet period.
          this.weeklyGoneQuietGroups.length
            ? { title: 'Gone quiet cleanup', type: 'groups', groups: this.weeklyGoneQuietGroups }
            : this.weeklyGoneQuietCleanup.length
              ? { title: 'Gone quiet cleanup', type: 'list', text: '', items: this.weeklyGoneQuietCleanup }
              : null,
          dataSourcesSection,
        ].filter(Boolean),
      }
    },
    qbrBrief() {
      return this.latestResponse?.qbrBrief || null
    },
    latestReportView() {
      return this.latestResponse?.reportView || null
    },
    qbrSections() {
      const brief = this.qbrBrief || {}
      // clientName and periodCovered are dropped — both are already shown in the
      // report header (title + date range), so repeating them here just duplicates
      // it. serviceRelationship isn't shown anywhere else, so it's kept.
      const serviceRelationship = brief.accountSnapshot?.serviceRelationship
        ? `Service Relationship: ${brief.accountSnapshot.serviceRelationship}`
        : null

      const sections = [
        {
          title: 'Summary',
          block: this.combineContentBlocks(
            brief.executiveSummary,
            brief.meetingObjective,
            serviceRelationship,
            brief.clientFacingSummary,
          ),
          defaultOpen: true,
        },
        {
          title: 'Service Review',
          block: this.combineContentBlocks(brief.keyWins, brief.workloadThemes),
        },
        {
          title: 'Risks & Follow-ups',
          block: this.combineContentBlocks(brief.serviceRisks, brief.risksNotYetConfirmed),
          groups: [
            { label: 'Open Questions', block: this.combineContentBlocks(brief.openQuestions) },
          ],
        },
        {
          title: 'Opportunities',
          block: this.combineContentBlocks(brief.nextQuarterFocus),
          groups: [
            { label: 'Recommended Agenda', block: this.combineContentBlocks(brief.recommendedAgenda) },
            { label: 'Talking Points', block: this.combineContentBlocks(brief.recommendedTalkingPoints) },
            { label: 'Client Questions', block: this.combineContentBlocks(brief.clientQuestions) },
          ],
        },
        {
          title: 'Data Sources & Limits',
          block: this.combineContentBlocks(brief.evidenceAndCaveats, this.psaAttributionNote),
          groups: [
            { label: 'Internal-Only Observations', block: this.combineContentBlocks(brief.internalOnlyObservations) },
          ],
        },
      ]

      // Story 30.10: client-safe, code-computed metrics table — omitted entirely (not a
      // zero-filled table) when generation didn't produce one, same rule as the exports.
      if (this.qbrMetricsTableRows.length > 0) {
        sections.push({
          title: 'By the Numbers',
          block: this.buildTableBlock(
            ['Metric', 'This Quarter', 'Prior Quarter', 'Change'],
            this.qbrMetricsTableRows,
          ),
        })
      }

      // Story 30.11: demo's headline "wow" — per-engineer hours + key-person-dependency flag.
      // The frontend is an internal tool (not a client-facing surface, per the audience-split
      // rule's own wording), so there's no internalOnly concept to apply here — this section
      // always renders in the UI when data exists, same as metricsTable above. The pptx/docx
      // split (internal-only vs client-safe) lives entirely in the backend's qbrReportSections.js.
      if (this.qbrEngineerBreakdownRows.length > 0) {
        sections.push({
          title: 'Engineer Breakdown',
          block: this.buildTableBlock(
            ['Engineer', 'This Quarter', 'Prior Quarter', 'Dependency'],
            this.qbrEngineerBreakdownRows,
          ),
        })
      }

      // Story 30.9: contract-decision table (keep/adjust/reprice/exit) with evidence derived
      // only from hours and ticket metrics (no contract/agreement data exists in this system —
      // see qbrReportMetrics.service.js's computeQbrContractDecision). Internal tool, like
      // Engineer Breakdown above — no internalOnly concept here; the client-safe/internal-only
      // split for exports lives entirely in the backend's qbrReportSections.js.
      if (this.qbrContractDecisionGroups.length > 0) {
        sections.push({
          title: 'Contract Decision',
          block: this.combineContentBlocks(),
          groups: this.qbrContractDecisionGroups,
        })
      }

      // Story 30.13: lives inside qbrBrief (LLM-authored), unlike metricsTable/engineerBreakdown
      // above — omitted entirely when empty (first-ever QBR for this client, or a custom date
      // range with no well-defined prior quarter).
      if (Array.isArray(brief.followThroughOnLastQuarter) && brief.followThroughOnLastQuarter.length > 0) {
        sections.push({
          title: 'Follow-Through on Last Quarter',
          block: this.combineContentBlocks(
            brief.followThroughOnLastQuarter.map(
              (item) => `${item.priority} — ${this.formatFollowThroughStatus(item.status)}: ${item.evidence}`,
            ),
          ),
        })
      }

      // Story 30.14 (trimmed): forward-looking watch-list, built from metricsTable (not
      // recomputed) — omitted entirely when there's nothing to watch, same rule as its siblings.
      if (this.qbrWatchListRows.length > 0) {
        sections.push({
          title: 'Watch List for Next Quarter',
          block: this.buildTableBlock(
            ['Metric', "This Quarter's Baseline", 'What Improvement Looks Like'],
            this.qbrWatchListRows,
          ),
        })
      }

      // Notable Backlog — code-computed (qbrReportMetrics.service.js's
      // computeQbrNotableBacklog), not LLM-authored, same omit-when-empty rule as its
      // siblings above. Never wired into the UI before (Phil's 2026-10-15 dry-run finding) —
      // the DOCX export has rendered it as a single-bullet note since it was built
      // (qbrReportSections.js), this mirrors that.
      const notableBacklogBlock = this.combineContentBlocks(this.latestResponse?.notableBacklog?.note)
      if (notableBacklogBlock.hasContent) {
        sections.push({
          title: 'Notable Backlog',
          block: notableBacklogBlock,
        })
      }

      // All sections always render (AC1) — even one with no content for this client still
      // shows its header, so the accordion structure stays stable.
      return sections.map((section) => ({
        ...section,
        groups: (section.groups || []).filter((group) => group.block.hasContent),
      }))
    },
    qbrMetricsTableRows() {
      const rows = this.latestResponse?.metricsTable?.rows
      if (!Array.isArray(rows)) return []
      return rows.map((row) => [
        row.label,
        this.formatMetricValue(row.current, { hours: QBR_HOURS_METRIC_LABELS.has(row.label) }),
        this.formatPriorMetricValue(row.prior, { hours: QBR_HOURS_METRIC_LABELS.has(row.label) }),
        this.formatMetricChangePct(row.changePct),
      ])
    },
    // Story 30.11: deterministic per-engineer hours + key-person-dependency flag.
    qbrEngineerBreakdownRows() {
      const engineers = this.latestResponse?.engineerBreakdown?.engineers
      if (!Array.isArray(engineers)) return []
      return engineers.map((e) => [
        e.name,
        this.formatMetricValue(e.currentHours, { hours: true }),
        this.formatPriorMetricValue(e.priorHours, { hours: true }),
        e.dependencyFlag || '—',
      ])
    },
    // Story 30.14 (trimmed): forward-looking watch-list rows, baseline values matching
    // metricsTable exactly (no independent recomputation — see qbrReportMetrics.service.js).
    qbrWatchListRows() {
      const items = this.latestResponse?.watchList?.items
      if (!Array.isArray(items)) return []
      return items.map((item) => [
        item.metric,
        this.formatMetricValue(item.baseline, { hours: QBR_HOURS_METRIC_LABELS.has(item.metric) }),
        item.note || '—',
      ])
    },
    // Story 30.9: deterministic contract-decision groups (one per option), evidence built only
    // from hours/ticket metrics — no contract or agreement data exists in this system.
    qbrContractDecisionGroups() {
      const options = this.latestResponse?.contractDecision?.options
      if (!Array.isArray(options)) return []
      return options.map((opt) => ({
        label: this.formatContractOptionLabel(opt.option),
        block: this.combineContentBlocks(
          `For: ${Array.isArray(opt.evidenceFor) && opt.evidenceFor.length ? opt.evidenceFor.join('; ') : 'No supporting evidence found'}`,
          `Against: ${Array.isArray(opt.evidenceAgainst) && opt.evidenceAgainst.length ? opt.evidenceAgainst.join('; ') : 'No opposing evidence found'}`,
        ),
      }))
    },
    // Deterministic PSA attribution caveat, computed server-side (ask.service.js's
    // buildAttributionNote) — merged into whichever mode-specific "Data Sources & Limits"
    // equivalent section is showing, since QBR/Coaching/Evidence Review render their own
    // LLM-generated fields instead of reportView.dataContextAndLimits.
    psaAttributionNote() {
      return this.latestResponse?.reportView?.dataContextAndLimits?.attributionNote || null
    },
    evidenceReview() {
      return this.latestResponse?.evidenceReview || null
    },
    evidenceReviewItems() {
      const items = Array.isArray(this.evidenceReview?.evidenceItems)
        ? this.evidenceReview.evidenceItems
        : []

      return items
        .map((item) => ({
          title: this.pickFirst(item?.title, item?.label, 'Evidence item'),
          detail: String(item?.detail || item?.summary || '').trim(),
          refs: this.uniqueTrimmed(Array.isArray(item?.refs) ? item.refs : this.splitStructuredText(item?.refs)),
        }))
        .filter((item) => item.title || item.detail || item.refs.length)
    },
    evidenceReviewFollowupQuestions() {
      return this.normalizeListContent(this.evidenceReview?.followupQuestions)
    },
    evidenceReviewCaveats() {
      const caveats = this.normalizeListContent(this.evidenceReview?.caveats)
      return this.psaAttributionNote ? [...caveats, this.psaAttributionNote] : caveats
    },
    coachingBrief() {
      return this.latestResponse?.coachingBrief || null
    },
    coachingBriefSections() {
      const brief = this.coachingBrief || {}
      return [
        { title: 'Coaching Objective', block: this.normalizeContentBlock(brief.coachingObjective) },
        { title: 'Observed Themes', block: this.normalizeContentBlock(brief.observedThemes) },
        { title: 'Strengths To Reinforce', block: this.normalizeContentBlock(brief.strengthsToReinforce) },
        { title: 'Concerns To Address', block: this.normalizeContentBlock(brief.concernsToAddress) },
        { title: 'Coaching Actions', block: this.normalizeContentBlock(brief.coachingActions) },
        { title: 'Talking Points', block: this.normalizeContentBlock(brief.talkingPoints) },
        { title: 'Follow-up Plan', block: this.normalizeContentBlock(brief.followupPlan) },
        { title: 'Data Context and Limits', block: this.combineContentBlocks(brief.evidenceAndCaveats, this.psaAttributionNote) },
      ].filter((section) => section.block.hasContent)
    },
    datasetContextItems() {
      const reportViewItems = Array.isArray(this.latestResponse?.reportView?.dataContextAndLimits?.items)
        ? this.latestResponse.reportView.dataContextAndLimits.items
            .map((item) => ({
              label: String(item?.label || '').trim(),
              value: String(item?.value || '').trim(),
            }))
            .filter((item) => item.label && item.value)
        : []
      if (reportViewItems.length) {
        return reportViewItems
      }

      const items = [
        { label: 'Source', value: this.sourceDataLabel },
        { label: 'Analyzed date range', value: this.displayedDateRange },
        { label: 'Clients represented', value: this.formatCountLabel(this.latestMeta.clientCount, 'client') },
        { label: 'Team members represented', value: this.formatCountLabel(this.latestMeta.employeeCount, 'team member') },
        { label: 'Data type', value: this.latestMeta.isDemoData ? 'Demo data' : '' },
        { label: 'Last synced', value: this.formatSyncTime(this.latestMeta) },
      ]

      return items.filter((item) => String(item.value || '').trim())
    },
    trustNotices() {
      const notices = Array.isArray(this.responsePolicy?.notices) ? this.responsePolicy.notices : []
      return this.uniqueTrimmed(notices)
    },
    trustSignalLabel() {
      const strength = this.signalStrength?.strength
      if (strength === 'HIGH') return 'Strong signal'
      if (strength === 'MODERATE') return 'Medium signal'
      if (strength === 'LOW') return 'Weak signal'
      if (strength === 'INSUFFICIENT') return 'Insufficient signal'
      return ''
    },
    trustSignalTooltip() {
      const strength = this.signalStrength?.strength
      if (strength === 'HIGH') return 'The evidence looks strong enough to support a confident operating conclusion.'
      if (strength === 'MODERATE') return 'The evidence suggests a likely pattern, but it still needs validation before heavier action.'
      if (strength === 'LOW') return 'The evidence is weak or narrow, so this should be treated as an investigation prompt rather than a conclusion.'
      if (strength === 'INSUFFICIENT') return 'There is not enough evidence to answer this safely without more review.'
      return ''
    },
    trustPolicyLabel() {
      const answerMode = String(this.responsePolicy?.answerMode || '').trim()
      return answerMode ? this.humanizeLabel(answerMode) : ''
    },
    trustPolicyTooltip() {
      const answerMode = String(this.responsePolicy?.answerMode || '').trim()
      if (answerMode === 'recommended_action') return 'The system is allowed to give concrete next steps because the evidence supports action-oriented guidance.'
      if (answerMode === 'likely_pattern') return 'The system sees a probable pattern and is steering you toward validation before stronger action.'
      if (answerMode === 'hypothesis') return 'The system is treating this as a hypothesis, so guidance should stay exploratory.'
      if (answerMode === 'insufficient_evidence') return 'The system does not have enough evidence to provide a safe conclusion.'
      if (answerMode === 'blocked') return 'The system intentionally stopped because the evidence or integrity checks make the answer unsafe.'
      return ''
    },
    trustReviewLabel() {
      const reviewStatus = String(this.responsePolicy?.reviewStatus || '').trim()
      return reviewStatus ? this.humanizeLabel(reviewStatus) : ''
    },
    trustReviewTooltip() {
      const reviewStatus = String(this.responsePolicy?.reviewStatus || '').trim()
      if (reviewStatus === 'ready_for_manager_use') return 'This answer is in the safest state available in the current policy and does not require extra review before manager use.'
      if (reviewStatus === 'review_required') return 'A human should review this answer before treating it as fully cleared, usually because the data is provisional, trust-sensitive, or demo-backed.'
      if (reviewStatus === 'blocked') return 'The answer should not be used until the underlying evidence or integrity issue is resolved.'
      return ''
    },
    integrityIssuesForUi() {
      const issues = Array.isArray(this.integrityCheck?.issues) ? this.integrityCheck.issues : []
      return issues
        .map((issue) => ({
          severityLabel: this.humanizeLabel(issue?.severity || 'issue'),
          message: String(issue?.message || '').trim(),
        }))
        .filter((issue) => issue.message)
    },
    evidenceFactorsForUi() {
      const factors = Array.isArray(this.evidenceProfile?.factors) ? this.evidenceProfile.factors : []
      return factors
        .map((factor) => ({
          label: String(factor?.label || '').trim(),
          value: `${factor?.actual ?? 0} / ${factor?.threshold ?? 0}${factor?.met ? ' met' : ' below threshold'}`,
        }))
        .filter((factor) => factor.label)
    },
    showTrustPanel() {
      return !this.hasIntegrityBlock && !this.hasStructuredFallback && Boolean(
        this.trustSignalLabel ||
        this.trustPolicyLabel ||
        this.trustReviewLabel ||
        this.trustNotices.length ||
        this.integrityIssuesForUi.length ||
        this.evidenceFactorsForUi.length,
      )
    },
    displayedDateRange() {
      const metaRange = this.formatDateRange(this.latestMeta)
      if (metaRange) return metaRange

      return 'No valid activity date available'
    },
    resultHeaderEyebrow() {
      return 'Report'
    },
    resultHeaderTitle() {
      const explicitTitle = String(
        this.latestResponse?.displayQuestion ||
        this.latestResponse?.reportView?.title ||
        this.latestResponse?.question ||
        '',
      ).trim()
      return explicitTitle || 'Insight Workspace report'
    },
    resultHeaderMeta() {
      if (!this.latestResponse || this.latestResponse.loading || this.latestResponse.error) return ''
      return this.responseContextSummary
    },
    canExportReport() {
      return Boolean(
        this.latestResponse &&
        !this.latestResponse.loading &&
        !this.latestResponse.error &&
        !this.hasIntegrityBlock &&
        !this.hasStructuredFallback &&
        !this.isAccessDenied &&
        !this.isInsufficientSignalMode,
      )
    },
    responseContextSummary() {
      const parts = []
      const dateRange = this.displayedDateRange
      const citationCount = Array.isArray(this.latestResponse?.citations) ? this.latestResponse.citations.length : 0

      if (dateRange) parts.push(dateRange)
      if (citationCount) parts.push(`${citationCount} citations`)

      return parts.join(' | ')
    },
    sanitizedAnswerHtml() {
      const answerHtml = String(this.latestResponse?.answerHtml || '').trim()
      if (!answerHtml) return ''

      return this.sanitizeAnswerHtml(answerHtml)
    },
    responseTextContent() {
      if (this.sanitizedAnswerHtml) {
        return this.extractPlainText(this.sanitizedAnswerHtml)
      }

      return String(this.latestResponse?.answer || '')
    },
  },
  watch: {
    // KAN-81: the signed-in user's clicked tickets, so report links already opened show as visited.
    'user._id': {
      immediate: true,
      handler(userId) {
        loadVisitedTickets(userId)
      },
    },
    isManager: {
      immediate: false,
      handler(newValue) {
        if (!newValue && this.selectedScope === 'manager') {
          this.selectedScope = 'self'
        }
      },
    },
    isAdmin: {
      immediate: false,
      handler(newValue) {
        if (!newValue && this.selectedScope === 'executive') {
          this.selectedScope = this.isManager ? 'manager' : 'self'
        }
      },
    },
    availableScopeTypes: {
      immediate: true,
      handler(newScopes) {
        if (!Array.isArray(newScopes) || !newScopes.length) {
          this.selectedScope = this.defaultScope
          return
        }

        this.selectedScope = this.resolvePreferredScope(newScopes)
      },
    },
    availableDateRangePresets: {
      immediate: true,
      handler(newPresets) {
        const presetValues = Array.isArray(newPresets) ? newPresets.map((preset) => preset.value) : []
        if (!presetValues.length) {
          this.selectedDateRangePreset = 'all_time'
          return
        }

        if (!presetValues.includes(this.selectedDateRangePreset)) {
          this.selectedDateRangePreset = presetValues[0]
        }
      },
    },
    questionDraft(newValue) {
      if (!this.selectedPromptText) return

      const trimmedValue = String(newValue || '').trim()
      if (trimmedValue !== this.selectedPromptText) {
        this.selectedPromptText = ''
        this.selectedPromptOutputMode = ''
      }
    },
    user: {
      immediate: true,
      handler(newUser) {
        if (this.selectedScope === 'self' && !String(this.localSelectedTeamMemberName || '').trim()) {
          this.localSelectedTeamMemberName = `${newUser?.firstName || ''} ${newUser?.lastName || ''}`.trim()
        }
        if (!Array.isArray(newUser?.roster) || !newUser.roster.length) {
          this.rosterMembers = []
          return
        }

        this.fetchRosterOptions()
      },
    },
    selectedClientName: {
      immediate: true,
      handler(newValue) {
        const normalized = String(newValue || '').trim()
        if (normalized !== this.localSelectedClientName) {
          this.localSelectedClientName = normalized
        }
      },
    },
    selectedTeamMemberName: {
      immediate: true,
      handler(newValue) {
        const normalized = String(newValue || '').trim()
        if (normalized !== this.localSelectedTeamMemberName) {
          this.localSelectedTeamMemberName = normalized
        }
      },
    },
    selectedScope: {
      immediate: true,
      handler(newScope) {
        if (newScope === 'self' && !String(this.localSelectedTeamMemberName || '').trim()) {
          this.localSelectedTeamMemberName = `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim()
        }
      },
    },
    isMetadataLoading(newValue) {
      if (!newValue) {
        this.$nextTick(() => this.tryAutoRunAction())
      }
    },
    // Vue Router doesn't remount this component on a query-only navigation (e.g. clicking a
    // second /last-working-day?ticket=X link while already on this page) — without this,
    // the one-shot initialTicketDrawerOpened guard would silently block the second deep link.
    // The already-loaded report's ticketRefs stays valid, so re-opening here (rather than
    // re-running the whole auto-run action) is enough.
    initialTicketNbr(newValue) {
      if (!newValue) return
      this.initialTicketDrawerOpened = false
      this.maybeOpenInitialTicketDrawer()
    },
  },
  created() {
    this.fetchAskMetadata()
    this.fetchRosterOptions()
  },
  beforeUnmount() {
    clearTimeout(this.turnHighlightTimer)
    this.closeAskStream()
    this.stopLoadingPhraseRotation()
  },
  methods: {
    scopeLabelFor(type, fallbackLabel = '') {
      return fallbackLabel || DEFAULT_SCOPE_LABELS[type] || type
    },
    resolvePreferredScope(scopes = []) {
      const availableScopes = Array.isArray(scopes) ? scopes : []

      // Phil (2026-10-16): the server only offers executive to company admins (isAdmin, same
      // company), and for them Company overview is the useful default for company-wide questions.
      if (availableScopes.includes('executive')) return 'executive'
      if (availableScopes.includes('manager')) return 'manager'
      if (availableScopes.includes('self')) return 'self'

      return this.defaultScope
    },
    parseDateValue(value) {
      if (!value) return null
      if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
      if (typeof value === 'number') {
        const parsed = new Date(value)
        return Number.isNaN(parsed.getTime()) ? null : parsed
      }

      const normalized = String(value).trim()
      if (!normalized) return null

      const utcLikeValue = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)?$/.test(normalized)
        ? normalized.replace(' ', 'T') + 'Z'
        : normalized
      const parsed = new Date(utcLikeValue)

      return Number.isNaN(parsed.getTime()) ? null : parsed
    },
    toggleWorkspaceArea(areaId) {
      if (this.isBusy) return
      this.expandedWorkspaceArea = this.expandedWorkspaceArea === areaId ? '' : areaId
    },
    tryAutoRunAction() {
      if (!this.autoRunActionId || this.hasAutoRunAction || this.isBusy) return

      const action = this.managerQuickStartActions.find((item) => item.id === this.autoRunActionId)
      if (!action) return

      this.hasAutoRunAction = true
      this.runWorkspaceAction(action)
    },
    buildWorkspacePromptContext(action = null) {
      const contextDateRangeLabel = action?.dateRangePreset
        ? this.formatPresetDateRange(action.dateRangePreset)
        : this.currentDateRangeContextLabel

      return {
        scopeLabel: this.activeScopeLabel,
        selectedClientName: this.effectiveSelectedClientName,
        selectedTeamMemberName: this.effectiveSelectedTeamMemberName,
        dateRangeLabel: contextDateRangeLabel,
        sourceDataLabel: this.sourceDataLabel,
      }
    },
    resolveWorkspaceActionDisplayQuestion(action) {
      return resolveWorkspaceActionDisplayQuestion(this.selectedScope, action, this.buildWorkspacePromptContext(action))
    },
    resolveWorkspaceActionHelperText(action) {
      return resolveWorkspaceActionHelperText(this.selectedScope, action, this.buildWorkspacePromptContext(action))
    },
    isLastWorkingDayAction(action) {
      return action?.id === 'review-last-working-day'
    },
    buildDefaultWorkspaceSource(sourceType = 'freeform') {
      return {
        sourceType,
        actionId: '',
        sectionId: this.activeWorkspaceArea?.id || '',
        selectedClientName: this.effectiveSelectedClientName,
        selectedTeamMemberName: this.effectiveSelectedTeamMemberName,
      }
    },
    // KAN-41 (Phil, 2026-10-06 sync): this used to also auto-trigger the "Choose What to
    // Analyze" modal for most real freeform questions (client/account/date-range keywords are
    // common in ordinary prompts). Phil's review (2026-10-06): that left users unsure what
    // happens if they skip it, and a keyword regex caught most real questions anyway. Kept only
    // for the explicit forceContext path below (a user deliberately opening the modal) — no
    // longer consulted for ordinary freeform submission. Ask now infers scope from the prompt
    // itself (backend, KAN-41) and asks a follow-up in the conversation when it can't.
    shouldPromptForFreeformContext(question = '') {
      const normalizedQuestion = String(question || '').trim().toLowerCase()
      if (!normalizedQuestion) return false
      if (this.effectiveSelectedClientName || this.effectiveSelectedTeamMemberName) return false
      if (this.selectedDateRangePreset && this.selectedDateRangePreset !== 'all_time') return false

      return /\b(client|clients|account|accounts|customer|customers|team member|team members|engineer|engineers|technician|technicians|member|members|this week|last week|this month|last month|today|yesterday|date range|from\b|between\b)\b/i.test(normalizedQuestion)
    },
    submitDraftQuestion(options = {}) {
      if (!this.questionDraft || this.isBusy) return
      const forceContext = options?.forceContext === true
      const workspaceSource = this.buildDefaultWorkspaceSource('freeform')

      if (forceContext) {
        this.openContextModal({
          action: null,
          question: this.questionDraft,
          displayQuestion: this.questionDraft,
          outputMode: this.selectedPromptOutputMode || 'auto',
          workspaceSource,
          contextReason: 'freeform_explicit',
        })
        return
      }

      // spec-ask-ux-impersonation-and-composer: the composer empties once a question is sent,
      // whether it then succeeds or fails (a failed one stays visible in its turn with Retry),
      // so the next question is never glued onto the previous one.
      // A failure that can't be retried (e.g. a 4xx) puts the question back in the box so it can
      // be edited and resent — unless something new has been typed meanwhile.
      const question = this.questionDraft
      this.questionDraft = ''
      return this.submitConversationMessage(question, {
        displayQuestion: question,
      }).then((result) => {
        if (result?.failed && !result.canRetry && !this.questionDraft) this.questionDraft = question
        return result
      })
    },
    // spec-ask-ux-impersonation-and-composer: "New conversation" — clears the thread and its
    // conversationId so the next question starts a new conversation. Keeps the selected scope
    // and leaves saved History alone. No-op while a question is in flight.
    startNewConversation() {
      if (this.isAsking) return
      this.conversationMessages = []
      this.conversationId = null
      this.conversationNotFound = false
      this.conversationOpenError = ''
      this.highlightedTurnId = ''
    },
    showsOptionalContextOverride(action) {
      return action?.launchBehavior === 'defaults_then_optional_context'
    },
    openActionContext(action) {
      if (!action || this.isBusy) return
      const text = this.resolveWorkspaceActionPrompt(action)
      const displayQuestion = this.resolveWorkspaceActionDisplayQuestion(action)
      if (!text) return

      const workspaceSource = {
        sourceType: this.managerQuickStartActions.some((item) => item.id === action.id)
          ? 'quick_start'
          : 'area_prompt',
        actionId: action.id,
        sectionId: this.activeWorkspaceArea?.id || '',
        selectedClientName: this.effectiveSelectedClientName,
        selectedTeamMemberName: this.effectiveSelectedTeamMemberName,
      }

      this.openContextModal({
        action,
        question: text,
        displayQuestion,
        outputMode: action.outputMode || 'operational_insight',
        workspaceSource,
        contextReason: action.launchBehavior === 'needs_context' ? 'action_required' : 'action_optional',
      })
    },
    runWorkspaceAction(action) {
      if (!action || this.isBusy) return
      const text = this.resolveWorkspaceActionPrompt(action)
      const displayQuestion = this.resolveWorkspaceActionDisplayQuestion(action)
      if (!text) return

      this.questionDraft = ''
      this.selectedPromptText = ''
      this.selectedPromptOutputMode = ''
      // Workspace/quick-start actions render through the unchanged latestResponse card,
      // not the conversation thread — clear any active thread so the two result surfaces
      // never render at once, and so a later free-text question starts a fresh
      // conversation rather than silently appending to a now-hidden one.
      //
      // Story 30.4 finding (NOT implemented — see story Dev Agent Record for full writeup):
      // the only two catalog entries with launchBehavior 'runs_immediately'/
      // 'defaults_then_optional_context' are review-last-working-day and
      // review-last-working-week — the flagship Last Working Day/Week reports, including
      // the weeklyReport/OperationalInsightView specialized rendering this component's own
      // tests cover end-to-end. There is no quick-start action in today's catalog that
      // both (a) runs immediately without the context modal and (b) only ever produces
      // generic, non-specialized output. So "wire quick-start into conversations, accept
      // generic rendering" has no way to land here without concretely degrading one of
      // those two specific reports to plain text — a materially different, higher-stakes
      // call than the abstract trade-off this was approved against. Left unchanged
      // pending explicit re-confirmation naming these two features.
      this.conversationMessages = []
      this.conversationId = null
      this.conversationNotFound = false
      this.conversationOpenError = ''
      const workspaceSource = {
        sourceType: this.managerQuickStartActions.some((item) => item.id === action.id)
          ? 'quick_start'
          : 'area_prompt',
        actionId: action.id,
        sectionId: this.activeWorkspaceArea?.id || '',
        selectedClientName: this.effectiveSelectedClientName,
        selectedTeamMemberName: this.effectiveSelectedTeamMemberName,
      }

      if (action.launchBehavior === 'runs_immediately' || action.launchBehavior === 'defaults_then_optional_context') {
        this.submitQuestion(text, {
          outputMode: action.outputMode || 'operational_insight',
          dateRangePreset: action.dateRangePreset || this.selectedDateRangePreset,
          displayQuestion,
          workspaceSource,
        })
        return
      }

      this.openContextModal({
        action,
        question: text,
        displayQuestion,
        outputMode: action.outputMode || 'operational_insight',
        workspaceSource,
        contextReason: 'action_required',
      })
    },
    resolveWorkspaceActionPrompt(action) {
      if (!action) return ''
      return resolveWorkspaceActionQuestion(this.selectedScope, action, this.buildWorkspacePromptContext(action))
    },
    normalizeTeamMemberOption(entry) {
      if (!entry || typeof entry !== 'object') return null

      const firstName = String(entry.firstName || '').trim()
      const lastName = String(entry.lastName || '').trim()
      const fullName = `${firstName} ${lastName}`.trim()
      const value = fullName || String(entry.email || '').trim()
      if (!value) return null

      return {
        value,
        label: fullName || String(entry.email || '').trim(),
      }
    },
    normalizeOptionIdentity(value) {
      return String(value || '').trim().toLowerCase()
    },
    resolveOptionForSelection(selection, options = []) {
      const normalizedSelection = this.normalizeOptionIdentity(selection)
      if (!normalizedSelection) return null

      return options.find((option) => {
        const normalizedValue = this.normalizeOptionIdentity(option?.value)
        const normalizedLabel = this.normalizeOptionIdentity(option?.label)
        return normalizedSelection === normalizedValue || normalizedSelection === normalizedLabel
      }) || null
    },
    describeModalSelections(selections = [], options = []) {
      return selections
        .map((selection) => {
          const match = this.resolveOptionForSelection(selection, options)
          return match?.label || String(selection || '').trim()
        })
        .filter(Boolean)
        .join(', ')
    },
    canonicalizeSelections(selections = [], options = []) {
      const seen = new Set()
      return selections
        .map((selection) => {
          const match = this.resolveOptionForSelection(selection, options)
          return String(match?.value || selection || '').trim()
        })
        .filter((selection) => {
          const normalized = this.normalizeOptionIdentity(selection)
          if (!normalized || seen.has(normalized)) return false
          seen.add(normalized)
          return true
        })
    },
    reconcileModalSelections() {
      this.modalSelectedClients = this.canonicalizeSelections(
        this.modalSelectedClients,
        this.modalClientOptions,
      )
      this.modalSelectedTeamMembers = this.canonicalizeSelections(
        this.modalSelectedTeamMembers,
        this.modalTeamMemberOptions,
      )
    },
    startLoadingPhraseRotation() {
      this.stopLoadingPhraseRotation()
      this.loadingPhraseIndex = 0
      this.ellipsisCount = 1
      this.loadingPhraseIntervalId = window.setInterval(() => {
        const phraseCount = this.activeLoadingPhrases.length || 1
        this.loadingPhraseIndex = (this.loadingPhraseIndex + 1) % phraseCount
      }, 7000)
      this.ellipsisIntervalId = window.setInterval(() => {
        this.ellipsisCount = (this.ellipsisCount % 3) + 1
      }, 1000)
    },
    stopLoadingPhraseRotation() {
      if (this.loadingPhraseIntervalId) {
        window.clearInterval(this.loadingPhraseIntervalId)
        this.loadingPhraseIntervalId = null
      }
      if (this.ellipsisIntervalId) {
        window.clearInterval(this.ellipsisIntervalId)
        this.ellipsisIntervalId = null
      }
      this.loadingPhraseIndex = 0
      this.ellipsisCount = 1
    },
    closeAskStream() {
      if (this.askStreamController) {
        this.askStreamController.abort()
        this.askStreamController = null
      }
    },
    openTicketDrawer(ticketNbr) {
      // dataset values are always strings; ticketNbr from Snowflake may be a number — coerce both
      const needle = String(ticketNbr)
      loadVisitedTickets(this.user?._id)
      markTicketVisited(needle)
      const ticket = this.ticketRefs.find((t) => String(t.ticketNbr) === needle)
      // Fall back to a stub so the drawer still opens even if ticketRefs wasn't populated
      this.drawerTicket = ticket || { ticketNbr: needle }
      this.drawerOpen = true
    },
    maybeOpenInitialTicketDrawer() {
      if (!this.initialTicketNbr || this.initialTicketDrawerOpened) return
      this.initialTicketDrawerOpened = true
      this.openTicketDrawer(this.initialTicketNbr)
    },
    buildDefaultDateRangeForAction(action = null) {
      const endValue = this.pickFirst(
        this.askMetadata?.dateRange?.end,
        this.latestMeta.dateRangeEnd,
        new Date().toISOString(),
      )
      const endDate = this.parseDateValue(endValue) || new Date()
      const startDate = new Date(endDate)
      const preset = action?.dateRangePreset || this.selectedDateRangePreset

      if (preset === 'last_1_day') {
        startDate.setDate(startDate.getDate() - 1)
        endDate.setDate(endDate.getDate() - 1)
      } else if (preset === 'last_7_days') {
        startDate.setDate(startDate.getDate() - 6)
      } else if (preset === 'last_14_days') {
        startDate.setDate(startDate.getDate() - 13)
      } else if (preset === 'last_30_days') {
        startDate.setDate(startDate.getDate() - 29)
      } else {
        // No preset ("All time"): seed a trailing 7-day window anchored to today rather than
        // the earliest ticket we hold. Since the nightly sync started ingesting any recently
        // touched ticket (not just tickets closed in-window), askMetadata.dateRange.start can
        // reach years back — an old but still-active ticket drags the floor down. The
        // "Data available from" hint still exposes the full history for anyone who wants it.
        const today = new Date()
        const trailingStart = new Date(today)
        trailingStart.setDate(trailingStart.getDate() - 6)
        return { startDate: trailingStart, endDate: today }
      }

      return { startDate, endDate }
    },
    openContextModal({
      action = null,
      question = '',
      displayQuestion = '',
      outputMode = 'auto',
      workspaceSource = null,
      contextReason = '',
    } = {}) {
      const defaults = this.buildDefaultDateRangeForAction(action)
      this.pendingAction = action
      this.pendingQuestionText = String(question || '').trim()
      this.pendingDisplayQuestion = String(displayQuestion || question || '').trim()
      this.pendingOutputMode = outputMode || 'auto'
      this.pendingDateRangePreset = action?.dateRangePreset || this.selectedDateRangePreset
      this.pendingWorkspaceSource = workspaceSource
      this.pendingContextReason = contextReason
      const isQbr = QBR_ACTION_IDS.includes(action?.id)
      this.modalDateStart = isQbr ? null : defaults.startDate
      this.modalDateEnd = isQbr ? null : defaults.endDate
      this.modalSelectedClients = []
      this.modalSelectedTeamMembers = []
      this.reconcileModalSelections()
      this.isContextModalActive = true
    },
    closeContextModal() {
      this.isContextModalActive = false
      this.isModalSubmitting = false
      this.pendingAction = null
      this.pendingQuestionText = ''
      this.pendingDisplayQuestion = ''
      this.pendingOutputMode = 'auto'
      this.pendingDateRangePreset = 'all_time'
      this.pendingWorkspaceSource = null
      this.pendingContextReason = ''
    },
    resetModalSelections() {
      this.modalSelectedClients = []
      this.modalSelectedTeamMembers = []
      this.modalDateStart = null
      this.modalDateEnd = null
      this.localSelectedClientName = ''
      this.localSelectedTeamMemberName = ''
    },
    toggleModalClient(value) {
      const normalized = String(value || '').trim()
      if (!normalized) return
      if (this.modalSelectedClients.includes(normalized)) {
        this.modalSelectedClients = this.modalSelectedClients.filter((item) => item !== normalized)
      } else {
        this.modalSelectedClients = [...this.modalSelectedClients, normalized]
      }
    },
    toggleModalTeamMember(value) {
      const normalized = String(value || '').trim()
      if (!normalized) return
      if (this.modalSelectedTeamMembers.includes(normalized)) {
        this.modalSelectedTeamMembers = this.modalSelectedTeamMembers.filter((item) => item !== normalized)
      } else {
        this.modalSelectedTeamMembers = [...this.modalSelectedTeamMembers, normalized]
      }
    },
    toggleAllModalClients() {
      this.modalSelectedClients = this.areAllClientsSelected ? [] : this.modalClientOptions.map((option) => option.value)
    },
    handleQbrClientSelect(value) {
      const normalized = String(value || '').trim()
      this.modalSelectedClients = normalized ? [normalized] : []
    },
    toggleAllModalTeamMembers() {
      this.modalSelectedTeamMembers = this.areAllTeamMembersSelected ? [] : this.modalTeamMemberOptions.map((option) => option.value)
    },
    buildModalAnalysisContext() {
      const clientNames = this.canonicalizeSelections(this.modalSelectedClients, this.modalClientOptions)
        .map((selection) => {
          const match = this.resolveOptionForSelection(selection, this.modalClientOptions)
          return String(match?.value || selection || '').trim()
        })
        .filter(Boolean)
      // spec-ask-selection-label-accuracy.md (review patch): resolve against the option list and
      // DROP anything that doesn't match one. canonicalizeSelections passes unmatched selections
      // through verbatim (`match?.value || selection`), so without this filter a selection left
      // over from a prior scope — e.g. the scope switched to 'self' while the modal was open —
      // would still be submitted even though modalTeamMemberOptions no longer offers that person.
      const memberNames = this.canonicalizeSelections(this.modalSelectedTeamMembers, this.modalTeamMemberOptions)
        .map((selection) => {
          const match = this.resolveOptionForSelection(selection, this.modalTeamMemberOptions)
          return String(match?.value || '').trim()
        })
        .filter(Boolean)

      return {
        dateRange: {
          mode: 'custom',
          startDate: this.modalDateStart ? this.toLocalDateIso(this.modalDateStart) : null,
          endDate: this.modalDateEnd ? this.toLocalDateIso(this.modalDateEnd) : null,
        },
        clientSelection: {
          mode: clientNames.length ? 'subset' : 'all',
          clientNames,
        },
        teamMemberSelection: {
          mode: memberNames.length ? 'subset' : 'all',
          memberNames,
        },
      }
    },
    async submitContextModal() {
      if (!this.canSubmitContextModal || this.isModalSubmitting) return
      this.isModalSubmitting = true
      const analysisContext = this.buildModalAnalysisContext()
      const effectiveActionId = this.isQbrAction
        ? 'prepare-qbr'
        : this.pendingWorkspaceSource?.actionId
      const workspaceSource = this.pendingWorkspaceSource
        ? {
            ...this.pendingWorkspaceSource,
            actionId: effectiveActionId,
            selectedClientName: this.modalSelectedClients.length === 1 ? this.modalSelectedClients[0] : '',
            selectedTeamMemberName: this.modalSelectedTeamMembers.length === 1 ? this.modalSelectedTeamMembers[0] : '',
          }
        : null
      this.localSelectedClientName = this.modalSelectedClients.length === 1 ? this.modalSelectedClients[0] : ''
      this.localSelectedTeamMemberName = this.modalSelectedTeamMembers.length === 1 ? this.modalSelectedTeamMembers[0] : (
        this.selectedScope === 'self'
          ? `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim()
          : ''
      )
      this.isContextModalActive = false
      this.isModalSubmitting = false

      // Rebuild the question text with the actual modal-selected context so the AI isn't
      // told a stale label that was baked in at modal-open time.
      let effectiveQuestionText = this.pendingQuestionText
      if (this.pendingAction && this.modalDateStart && this.modalDateEnd) {
        const startLabel = this.formatDateOnly(this.toLocalDateIso(this.modalDateStart))
        const endLabel = this.formatDateOnly(this.toLocalDateIso(this.modalDateEnd))
        const actualDateLabel = (startLabel && endLabel)
          ? `${startLabel} to ${endLabel}`
          : this.currentDateRangeContextLabel
        effectiveQuestionText = resolveWorkspaceActionQuestion(
          this.selectedScope,
          this.pendingAction,
          {
            ...this.buildWorkspacePromptContext(this.pendingAction),
            dateRangeLabel: actualDateLabel,
          }
        )
      }

      const qbrDisplayLabel = 'Internal QBR Prep'
      const effectiveDisplayQuestion = this.isQbrAction
        ? (this.modalSelectedClients[0]
            ? `${qbrDisplayLabel} - ${this.modalSelectedClients[0]}`
            : qbrDisplayLabel)
        : (this.pendingDisplayQuestion || effectiveQuestionText)

      await this.submitQuestion(effectiveQuestionText, {
        outputMode: this.pendingOutputMode || 'auto',
        dateRangePreset: this.pendingDateRangePreset || this.selectedDateRangePreset,
        displayQuestion: effectiveDisplayQuestion,
        workspaceSource,
        analysisContext,
      })
      this.resetModalSelections()
    },
    buildStreamContextSummary() {
      return ''
    },
    extractStreamContextMeta(payload = {}) {
      // The backend's mid-stream "context" event ships its fields flat on the event payload
      // itself (not nested under .context/.meta/.datasetContext), while the "final" event's
      // date fields live nested under .datasetContext. Spreading the raw payload last picks up
      // the flat shape without disturbing the final event's nested merge (its top level has no
      // competing dateRange* keys).
      const candidate = { ...payload?.meta, ...payload?.datasetContext, ...payload?.context, ...payload }

      return {
        dateRangePreset: candidate.dateRangePreset,
        dateRangeLabel: candidate.dateRangeLabel,
        dateRangeStart: candidate.dateRangeStart,
        dateRangeEnd: candidate.dateRangeEnd,
        signalStrength: this.normalizeSignalStrength(candidate.signalStrength || payload?.signalStrength),
        responsePolicy: this.normalizeResponsePolicy(candidate.responsePolicy || payload?.responsePolicy),
        integrityCheck: this.normalizeIntegrityCheck(candidate.integrityCheck || payload?.integrityCheck),
        evidenceProfile: this.normalizeEvidenceProfile(candidate.evidenceProfile || payload?.evidenceProfile),
      }
    },
    parseStreamEvents(chunk) {
      const trimmedChunk = String(chunk || '').trim()
      if (!trimmedChunk) return []

      const ssePayload = trimmedChunk
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim())
        .join('\n')

      const normalizedChunk = ssePayload || trimmedChunk

      return normalizedChunk
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          try {
            return JSON.parse(line)
          } catch (error) {
            return null
          }
        })
        .filter(Boolean)
    },
    applyStreamStatus(payload = {}) {
      this.streamingStatusMessage =
        String(payload?.status?.message || payload?.message || '').trim() || 'Analyzing your question'

      const contextSummary = this.buildStreamContextSummary(payload)
      if (contextSummary) {
        this.streamingContextSummary = contextSummary
      }
      this.streamingContextMeta = this.extractStreamContextMeta(payload)
    },
    applyFinalAskPayload(data = {}) {
      const meta = this.normalizeMeta(data)
      const metaParts = []
      const volume = this.formatVolume(meta)
      const dateRange = this.formatDateRange(meta)
      const syncTime = this.formatSyncTime(meta)

      if (volume) metaParts.push(volume)
      if (dateRange) metaParts.push(dateRange)
      if (syncTime) metaParts.push(syncTime)
      if (meta?.model) metaParts.push(`Model: ${meta.model}`)

      this.latestResponse = {
        ...this.latestResponse,
        loading: false,
        raw: data.raw === true,
        accessDenied: data.accessDenied === true,
        answerHtml: data.answerHtml || '',
        answer: data.answer || 'No response received.',
        outputMode: this.normalizeOutputMode(data.outputMode),
        signalStrength: this.normalizeSignalStrength(data.signalStrength || data.context?.signalStrength || data.datasetContext?.signalStrength),
        queryIntent: data.queryIntent || null,
        responsePolicy: this.normalizeResponsePolicy(data.responsePolicy || data.context?.responsePolicy),
        integrityCheck: this.normalizeIntegrityCheck(data.integrityCheck || data.context?.integrityCheck),
        evidenceProfile: this.normalizeEvidenceProfile(data.evidenceProfile || data.context?.evidenceProfile),
        decisionSupport: this.normalizeDecisionSupport(data.decisionSupport),
        yesterdayReport: this.normalizeYesterdayReport(data.yesterdayReport),
        weeklyReport: this.normalizeWeeklyReport(data.weeklyReport),
        qbrBrief: this.normalizeQbrBrief(data.qbrBrief),
        evidenceReview: this.normalizeEvidenceReview(data.evidenceReview),
        coachingBrief: this.normalizeCoachingBrief(data.coachingBrief),
        reportView: this.normalizeReportView(data.reportView),
        // Code-computed QBR "People" sections (Stories 30.9-30.14) — qbrSections already
        // renders these once present on latestResponse; they were just never copied over
        // from the final SSE payload (Phil's 2026-10-15 dry-run finding). Not LLM-authored,
        // unlike qbrBrief above, so passed through as-is rather than normalized.
        metricsTable: data.metricsTable || null,
        engineerBreakdown: data.engineerBreakdown || null,
        contractDecision: data.contractDecision || null,
        watchList: data.watchList || null,
        notableBacklog: data.notableBacklog || null,
        // KAN-71: code-computed per-tool rows for the LWD/LWW "What your tools did" section —
        // passed through as-is, like the QBR sections above. Absent on older cached reports.
        toolActivity: data.toolActivity || null,
        // KAN-95: code-computed daily Team table (already filtered per viewer by the backend),
        // passed through as-is. Absent on older cached reports and when computation failed.
        teamTable: data.teamTable || null,
        // KAN-103: the LWD v2 report (code-chosen items, model-worded and checked by the backend),
        // passed through as-is. Absent on v1 reports.
        lwdV2: data.lwdV2 && typeof data.lwdV2 === 'object' ? data.lwdV2 : null,
        // KAN-109: the weekly report's code-computed departed-staff cleanup, passed through as-is.
        // Absent on older reports and for companies not on LWD v2.
        departedCleanup: data.departedCleanup && typeof data.departedCleanup === 'object' ? data.departedCleanup : null,
        // KAN-111: the weekly report's long-quiet tickets, by owner, passed through the same way.
        goneQuietCleanup: data.goneQuietCleanup && typeof data.goneQuietCleanup === 'object' ? data.goneQuietCleanup : null,
        citations: Array.isArray(data.citations) ? data.citations : [],
        limitations: Array.isArray(data.limitations) ? data.limitations : [],
        recommendations: Array.isArray(data.recommendedActions) ? data.recommendedActions : [],
        meta,
        metaLine: metaParts.join(' | '),
        error: '',
        canRetry: false,
      }
    },
    async streamAskResponse(question, scopeType, token, dateRangePreset, outputMode, workspaceSource = null, analysisContext = null, rawMode = false, rawDataOnly = false) {
      this.closeAskStream()
      this.askStreamController = new AbortController()

      const requestBody = {
        scopeType,
        question,
        dateRangePreset,
      }

      if (this.normalizeOutputMode(outputMode) || outputMode === 'auto') {
        requestBody.outputMode = outputMode
      }
      if (rawMode === true) {
        requestBody.rawMode = true
      }
      if (rawDataOnly === true) {
        requestBody.rawDataOnly = true
      }
      if (workspaceSource && typeof workspaceSource === 'object') {
        requestBody.workspaceSource = {
          sourceType: String(workspaceSource.sourceType || '').trim(),
          actionId: String(workspaceSource.actionId || '').trim(),
          sectionId: String(workspaceSource.sectionId || '').trim(),
          selectedClientName: String(workspaceSource.selectedClientName || '').trim(),
          selectedTeamMemberName: String(workspaceSource.selectedTeamMemberName || '').trim(),
        }
      }
      if (analysisContext && typeof analysisContext === 'object') {
        requestBody.analysisContext = analysisContext
      }

      const response = await fetch(`${process.env.VUE_APP_API_URL}/ask/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(requestBody),
        signal: this.askStreamController.signal,
      })

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null)
        const error = new Error(errorPayload?.message || 'Unable to get a response right now.')
        error.response = {
          status: response.status,
          data: errorPayload || {},
        }
        throw error
      }

      this.streamingStatusMessage = 'Analyzing your question'

      if (!response.body) {
        throw new Error('Streaming response body is unavailable.')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let finalPayload = null

      let streamComplete = false

      while (!streamComplete) {
        const { value, done } = await reader.read()
        if (done) {
          streamComplete = true
          continue
        }

        buffer += decoder.decode(value, { stream: true })
        const eventChunks = buffer.split('\n\n')
        buffer = eventChunks.pop() || ''

        eventChunks.flatMap((chunk) => this.parseStreamEvents(chunk)).forEach((payload) => {
          const eventType = String(payload?.type || payload?.event || '').trim()

          if (eventType === 'status' || payload?.status?.message || payload?.message) {
            this.applyStreamStatus(payload)
            return
          }

          if (eventType === 'final' || payload?.final) {
            finalPayload = payload?.final || payload?.data || payload
            const contextSummary = this.buildStreamContextSummary(payload)
            if (contextSummary) {
              this.streamingContextSummary = contextSummary
            }
            this.streamingContextMeta = this.extractStreamContextMeta(payload)
            return
          }

          if (eventType === 'context') {
            this.streamingContextMeta = this.extractStreamContextMeta(payload)
            return
          }

          if (eventType === 'ticket_refs') {
            if (Array.isArray(payload?.tickets)) {
              this.ticketRefs = payload.tickets
            }
            return
          }

          if (eventType === 'error' || payload?.error) {
            const message = payload?.error?.message || payload?.message || 'Unable to get a response right now.'
            const error = new Error(message)
            error.response = { status: 500, data: payload?.error || { message } }
            throw error
          }
        })
      }

      if (!finalPayload && buffer.trim()) {
        const trailingPayloads = this.parseStreamEvents(buffer)
        trailingPayloads.forEach((payload) => {
          const eventType = String(payload?.type || payload?.event || '').trim()
          if (eventType === 'final' || payload?.final) {
            finalPayload = payload?.final || payload?.data || payload
          } else if (eventType === 'context') {
            this.streamingContextMeta = this.extractStreamContextMeta(payload)
          } else if (eventType === 'ticket_refs') {
            if (Array.isArray(payload?.tickets)) this.ticketRefs = payload.tickets
          } else if (eventType === 'status' || payload?.status?.message || payload?.message) {
            this.applyStreamStatus(payload)
          }
        })
      }

      this.askStreamController = null

      if (!finalPayload) {
        throw new Error('Stream completed without a final response payload.')
      }

      return finalPayload
    },
    normalizeOutputMode(value) {
      const mode = String(value || '').trim()
      if (['operational_insight', 'qbr_brief', 'evidence_review', 'coaching_brief', 'insufficient_signal'].includes(mode)) {
        return mode
      }

      return ''
    },
    pickFirst(...values) {
      return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '')
    },
    formatDataSource(value) {
      if (!value) return ''
      if (value === 'connectwise_api') return 'ConnectWise PSA data'
      // The backend reports the company's active PSA; without this an Autotask tenant read "Autotask Api".
      if (value === 'autotask_api') return 'Autotask PSA data'

      return String(value)
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    },
    uniqueTrimmed(values) {
      return [...new Set(values.map((value) => String(value).replace(/\s+/g, ' ').trim()).filter(Boolean))]
    },
    formatTimestamp(value) {
      if (!value) return ''

      const parsed = this.parseDateValue(value)
      if (!parsed || parsed.getUTCFullYear() < 2000) return ''

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(parsed)
    },
    formatDateRange(meta) {
      const start = this.pickFirst(meta.dateRangeStart, meta.rangeStart, meta.startDate, meta.fromDate)
      const end = this.pickFirst(meta.dateRangeEnd, meta.rangeEnd, meta.endDate, meta.toDate)

      if (!start && !end && meta.dateRangeLabel) {
        return meta.dateRangeLabel
      }

      const formattedStart = this.formatDateOnly(start)
      const formattedEnd = this.formatDateOnly(end)

      if (formattedStart && formattedEnd) {
        if (formattedStart === formattedEnd) return formattedStart
        return `${formattedStart} to ${formattedEnd}`
      }

      return formattedStart || formattedEnd || ''
    },
    formatDateOnly(value) {
      if (!value) return ''

      const parsed = this.parseDateValue(value)
      if (!parsed || parsed.getUTCFullYear() < 2000) return ''

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(parsed)
    },
    toLocalDateIso(date) {
      if (!date) return null
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    lastBusinessDayBefore(date) {
      const d = new Date(date)
      d.setDate(d.getDate() - 1)
      while (d.getDay() === 0 || d.getDay() === 6) {
        d.setDate(d.getDate() - 1)
      }
      return d
    },
    formatPresetDateRange(preset) {
      if (!preset) return ''

      if (preset === 'all_time') {
        return this.formatDateRange(this.latestMeta) || this.formatDateRange(this.askMetadata?.dateRange || {})
      }

      if (preset === 'last_1_day') {
        const effectiveDay = this.lastBusinessDayBefore(new Date())
        const label = this.formatDateOnly(effectiveDay)
        return label ? `${label} to ${label}` : ''
      }

      const endValue = this.pickFirst(
        this.latestMeta.dateRangeEnd,
        this.askMetadata?.dateRange?.end,
        new Date().toISOString(),
      )
      const endDate = this.parseDateValue(endValue)
      if (!endDate) return ''

      const presetToDays = {
        last_30_days: 30,
        last_14_days: 14,
        last_7_days: 7,
      }
      const days = presetToDays[preset]
      if (!days) return ''

      const startDate = new Date(endDate)
      startDate.setDate(startDate.getDate() - (days - 1))

      return `${this.formatDateOnly(startDate)} to ${this.formatDateOnly(endDate)}`
    },
    formatSyncTime(meta) {
      const syncValue = this.pickFirst(meta.lastSyncedAt, meta.syncedAt, meta.lastUpdatedAt, meta.lastRefreshAt)
      return this.formatTimestamp(syncValue)
    },
    formatVolume(meta) {
      const recordCount = Number(meta.recordCount)
      if (Number.isFinite(recordCount) && recordCount >= 0) {
        return `${recordCount} tickets reviewed`
      }

      return ''
    },
    normalizeSignalStrength(value) {
      if (!value || typeof value !== 'object') return null

      const strength = String(value.strength || '').trim().toUpperCase()
      const mode = String(value.mode || '').trim()

      return {
        strength: ['INSUFFICIENT', 'LOW', 'MODERATE', 'HIGH'].includes(strength) ? strength : '',
        mode: ['insufficient_evidence', 'overview', 'likely_pattern', 'decision_support'].includes(mode) ? mode : '',
        score: Number.isFinite(Number(value.score)) ? Number(value.score) : null,
        threshold: Number.isFinite(Number(value.threshold)) ? Number(value.threshold) : null,
      }
    },
    normalizeResponsePolicy(value) {
      if (!value || typeof value !== 'object') return null

      return {
        answerMode: String(value.answerMode || '').trim(),
        recommendationLevel: String(value.recommendationLevel || '').trim(),
        confidenceLabel: String(value.confidenceLabel || '').trim(),
        shouldBlock: value.shouldBlock === true,
        isProvisional: value.isProvisional === true,
        reviewRequired: value.reviewRequired === true,
        allowRecommendations: value.allowRecommendations === true,
        allowClientFacingLanguage: value.allowClientFacingLanguage === true,
        allowPersonalJudgment: value.allowPersonalJudgment === true,
        allowStaffingJudgment: value.allowStaffingJudgment === true,
        showEvidenceFirst: value.showEvidenceFirst === true,
        reviewStatus: String(value.reviewStatus || '').trim(),
        nextStepLabel: String(value.nextStepLabel || '').trim(),
        notices: Array.isArray(value.notices) ? value.notices : [],
      }
    },
    normalizeIntegrityCheck(value) {
      if (!value || typeof value !== 'object') return null

      return {
        status: String(value.status || '').trim(),
        issueCount: Number.isFinite(Number(value.issueCount)) ? Number(value.issueCount) : 0,
        issues: Array.isArray(value.issues) ? value.issues : [],
        summary: String(value.summary || '').trim(),
      }
    },
    normalizeEvidenceProfile(value) {
      if (!value || typeof value !== 'object') return null

      return {
        queryType: String(value.queryType || '').trim(),
        riskTier: String(value.riskTier || '').trim(),
        adequacyLevel: String(value.adequacyLevel || '').trim(),
        factors: Array.isArray(value.factors) ? value.factors : [],
        totals: value.totals && typeof value.totals === 'object' ? value.totals : {},
        isDemoData: value.isDemoData === true,
      }
    },
    normalizeDecisionSupport(value) {
      if (!value || typeof value !== 'object') return null

      const linkedEntities = value.linkedEntities && typeof value.linkedEntities === 'object'
        ? value.linkedEntities
        : value

      return {
        title: String(value.title || '').trim(),
        mainTakeaway: value.mainTakeaway || value.topPriorityInsight || null,
        whyNow: String(value.whyNow || '').trim(),
        category: String(value.category || value.priorityCategory || '').trim(),
        confidence: String(value.confidence || '').trim(),
        whatsHappening: String(value.whatsHappening || '').trim(),
        whyItMayBeHappening: String(value.whyItMayBeHappening || value.whyItsHappening || '').trim(),
        recommendedActions: Array.isArray(value.recommendedActions)
          ? value.recommendedActions
          : this.splitStructuredText(value.recommendedActions || value.whatToDoNext),
        patterns: Array.isArray(value.patterns) ? value.patterns : Array.isArray(value.patternInsights) ? value.patternInsights : [],
        linkedEntities: {
          clients: Array.isArray(linkedEntities.clients) ? linkedEntities.clients : [],
          engineers: Array.isArray(linkedEntities.engineers) ? linkedEntities.engineers : [],
          tickets: Array.isArray(linkedEntities.tickets) ? linkedEntities.tickets : [],
        },
        roleFocus: String(value.roleFocus || '').trim(),
      }
    },
    normalizeQbrBrief(value) {
      if (!value || typeof value !== 'object') return null

      return {
        accountSnapshot: value.accountSnapshot,
        executiveSummary: value.executiveSummary,
        meetingObjective: value.meetingObjective,
        keyWins: value.keyWins,
        serviceRisks: value.serviceRisks,
        workloadThemes: value.workloadThemes,
        recommendedAgenda: value.recommendedAgenda,
        recommendedTalkingPoints: value.recommendedTalkingPoints,
        clientQuestions: value.clientQuestions,
        openQuestions: value.openQuestions,
        nextQuarterFocus: value.nextQuarterFocus,
        risksNotYetConfirmed: value.risksNotYetConfirmed,
        internalOnlyObservations: value.internalOnlyObservations,
        clientFacingSummary: value.clientFacingSummary,
        evidenceAndCaveats: value.evidenceAndCaveats,
        // Story 30.13 — dropped here meant qbrSections' "Follow-Through on Last Quarter"
        // check always saw undefined, even when the LLM generated it (Phil's 2026-10-15
        // dry-run finding).
        followThroughOnLastQuarter: value.followThroughOnLastQuarter,
      }
    },
    normalizeEvidenceReview(value) {
      if (!value || typeof value !== 'object') return null

      return {
        thesis: value.thesis,
        evidenceItems: Array.isArray(value.evidenceItems) ? value.evidenceItems : [],
        patterns: value.patterns,
        caveats: value.caveats,
        followupQuestions: value.followupQuestions,
      }
    },
    normalizeCoachingBrief(value) {
      if (!value || typeof value !== 'object') return null

      return {
        coachingObjective: value.coachingObjective,
        observedThemes: value.observedThemes,
        strengthsToReinforce: value.strengthsToReinforce,
        concernsToAddress: value.concernsToAddress,
        coachingActions: value.coachingActions,
        talkingPoints: value.talkingPoints,
        followupPlan: value.followupPlan,
        evidenceAndCaveats: value.evidenceAndCaveats,
      }
    },
    normalizeReportView(value) {
      if (!value || typeof value !== 'object') return null

      const normalizedItems = Array.isArray(value?.dataContextAndLimits?.items)
        ? value.dataContextAndLimits.items
            .map((item) => ({
              label: String(item?.label || '').trim(),
              value: String(item?.value || '').trim(),
            }))
            .filter((item) => item.label && item.value)
        : []

      return {
        title: String(value.title || '').trim(),
        outputMode: String(value.outputMode || '').trim(),
        status: String(value.status || '').trim(),
        cause: String(value.cause || '').trim(),
        evidenceReviewed: String(value.evidenceReviewed || '').trim(),
        suggestedActions: Array.isArray(value.suggestedActions) ? value.suggestedActions : [],
        signalAndActionSummary: value.signalAndActionSummary && typeof value.signalAndActionSummary === 'object'
          ? {
            signalStrengthLabel: String(value.signalAndActionSummary.signalStrengthLabel || '').trim(),
            signalStrengthExplanation: String(value.signalAndActionSummary.signalStrengthExplanation || '').trim(),
            actionStatusLabel: String(value.signalAndActionSummary.actionStatusLabel || '').trim(),
            actionStatusExplanation: String(value.signalAndActionSummary.actionStatusExplanation || '').trim(),
            managerReviewNote: String(value.signalAndActionSummary.managerReviewNote || '').trim(),
          }
          : null,
        mainTakeaway: String(value.mainTakeaway || '').trim(),
        whatsHappening: String(value.whatsHappening || '').trim(),
        whyItMayBeHappening: value.whyItMayBeHappening && typeof value.whyItMayBeHappening === 'object'
          ? {
            whatDataSuggests: String(value.whyItMayBeHappening.whatDataSuggests || '').trim(),
            whatToVerify: String(value.whyItMayBeHappening.whatToVerify || '').trim(),
          }
          : null,
        recommendedActions: Array.isArray(value.recommendedActions) ? value.recommendedActions : [],
        patterns: Array.isArray(value.patterns) ? value.patterns : [],
        dataContextAndLimits: {
          items: normalizedItems,
          limitations: Array.isArray(value?.dataContextAndLimits?.limitations)
            ? value.dataContextAndLimits.limitations
            : [],
          demoNote: typeof value?.dataContextAndLimits?.demoNote === 'string'
            ? value.dataContextAndLimits.demoNote.trim() || null
            : null,
        },
      }
    },
    normalizeYesterdayReport(value) {
      if (!value || typeof value !== 'object') return null

      return {
        summary: value.summary,
        clients: value.clients,
        team: value.team,
        followUps: value.followUps,
        dataSources: value.dataSources,
        toolsSummary: value.toolsSummary,
      }
    },
    normalizeWeeklyReport(value) {
      if (!value || typeof value !== 'object') return null

      return {
        summary: value.summary,
        clients: value.clients,
        team: value.team,
        followUps: value.followUps,
        dataSources: value.dataSources,
        toolsSummary: value.toolsSummary,
      }
    },
    // KAN-71: lines for the LWD/LWW "What your tools did" section. The per-tool rows are the
    // backend's code-computed toolActivity (never model-written); toolsSummary is the model's
    // optional one-liner. No toolActivity (older reports, non-executive scope) means no section.
    buildToolActivityItems(toolActivity, toolsSummary) {
      if (!toolActivity || typeof toolActivity !== 'object') return []
      if (toolActivity.identified !== true) return ["Tool activity isn't identified for this PSA yet"]

      const items = []
      if (typeof toolsSummary === 'string' && toolsSummary.trim()) items.push(toolsSummary.trim())

      const tools = Array.isArray(toolActivity.tools) ? toolActivity.tools : []
      tools.forEach((tool) => {
        // Lead with the tool's total tickets in the period: "closed on its own" / "still New" /
        // "High/Critical" count every one of them, including tickets opened before the period, so
        // showing "opened" first read as "67 still New out of 35 opened" (Phil's 2026-10-16 run).
        const opened = Number(tool.opened || 0)
        const total = Math.max(Number(tool.ticketCount || 0), opened)
        const totalLabel = `${total} ticket${total === 1 ? '' : 's'}`
        const openedLabel = opened === total ? 'all opened this period' : `${opened} opened this period`
        // KAN-102: tickets the tool created and a person closed. Only shown when there are some, so
        // reports saved before the field existed read as before.
        const closedByTeam = Number(tool.closedByTeam || 0)
        const closedByTeamLabel = closedByTeam > 0 ? `${closedByTeam} closed by your team · ` : ''
        items.push(
          `${tool.toolName}: ${totalLabel} (${openedLabel}) · ${Number(tool.closedOnOwn || 0)} closed on its own · ` +
            `${closedByTeamLabel}${Number(tool.stillNew || 0)} still New · ${Number(tool.highOrCritical || 0)} High/Critical`,
        )
      })
      const additionalToolCount = Number(toolActivity.additionalToolCount || 0)
      if (additionalToolCount > 0) {
        items.push(`And ${additionalToolCount} more tool${additionalToolCount === 1 ? '' : 's'}`)
      }

      if (toolActivity.excludedByRules) {
        const excludedCount = Number(toolActivity.excludedToolTicketCount || 0)
        items.push(
          excludedCount > 0
            ? `${excludedCount} tool ticket${excludedCount === 1 ? ' was' : 's were'} excluded by this company's PSA rules`
            : "Tool tickets are excluded by this company's PSA rules",
        )
      } else if (!tools.length) {
        items.push('No tool activity in this period')
      }
      return items
    },
    buildYesterdayReportSection(title, items) {
      const normalizedItems = Array.isArray(items) ? items : this.normalizeListContent(items)
      if (!normalizedItems.length) return null

      return {
        title,
        type: normalizedItems.length === 1 ? 'text' : 'list',
        text: normalizedItems[0] || '',
        items: normalizedItems,
      }
    },
    isSectionHeaderItem(text) {
      const normalized = String(text || '').trim().toLowerCase().replace(/[:#\-*]/g, '').trim()
      const headers = [
        'data context and limits', 'data context', 'context and limits',
        'evidence and caveats', 'evidence and confidence notes',
        'limitations', 'limits', 'data limits',
        'source', 'last sync', 'last synced',
      ]
      return headers.includes(normalized)
    },
    filterSectionHeaders(items) {
      return items.filter((item) => !this.isSectionHeaderItem(item))
    },
    mergeFragmentedInitials(items) {
      const merged = []
      for (const item of items) {
        if (merged.length && /\s[A-Z]\.$/.test(merged[merged.length - 1])) {
          merged[merged.length - 1] = merged[merged.length - 1] + ' ' + item
        } else {
          merged.push(item)
        }
      }
      return merged
    },
    normalizeListContent(value) {
      if (Array.isArray(value)) {
        return this.uniqueTrimmed(
          this.filterSectionHeaders(
            this.mergeFragmentedInitials(
              value.flatMap((entry) => {
                if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
                  return this.describeObjectItem(entry)
                }

                return this.splitStructuredText(entry)
              }),
            ),
          ),
        )
      }

      if (value && typeof value === 'object') {
        return this.uniqueTrimmed(this.objectPairs(value).map((pair) => `${pair.label}: ${pair.value}`))
      }

      return this.splitStructuredText(value)
    },
    // Story 30.10: builds a 'table' block from the deterministic metricsTable payload sibling
    // (never present inside qbrBrief itself — it's code-computed, not LLM-authored). Mirrors
    // the backend's qbrReportSections.js formatting so the UI, pptx, and docx all agree.
    buildTableBlock(columns, rows) {
      return {
        hasContent: Array.isArray(rows) && rows.length > 0,
        type: 'table',
        text: '',
        items: [],
        pairs: [],
        columns,
        rows,
      }
    },
    // Hours always one decimal ("1.0", not "1"), counts plain integers — same shared
    // formatters as the backend export path (Phil's rule 7).
    formatMetricValue(value, { hours = false } = {}) {
      const formatted = hours ? formatHours(value) : formatCount(value)
      return formatted === null ? '—' : formatted
    },
    formatPriorMetricValue(value, options) {
      return value === null || value === undefined ? 'No prior-quarter data' : this.formatMetricValue(value, options)
    },
    formatMetricChangePct(value) {
      if (value === null || value === undefined) return '—'
      return `${value > 0 ? '+' : ''}${value}%`
    },
    // Story 30.9: labels for contractDecision.options' option enum.
    formatContractOptionLabel(option) {
      const labels = {
        keep: 'Keep as is',
        adjust: 'Adjust scope or terms',
        reprice: 'Reprice',
        exit: 'Exit',
      }
      return labels[option] || option || 'Unknown option'
    },
    // Story 30.13: labels for qbrBrief.followThroughOnLastQuarter's status enum.
    formatFollowThroughStatus(status) {
      const labels = {
        delivered: 'Delivered',
        partly_delivered: 'Partly delivered',
        went_backwards: 'Went backwards',
        // Phil (2026-10-16): evidence can't confirm delivery either way — matches the backend label.
        unverified: "Can't verify from ticket data",
      }
      return labels[status] || status || 'Unknown'
    },
    combineContentBlocks(...values) {
      const items = this.uniqueTrimmed(
        this.filterSectionHeaders(
          this.mergeFragmentedInitials(
            values.flatMap((value) => this.normalizeListContent(value)),
          ),
        ),
      )

      return {
        hasContent: items.length > 0,
        type: items.length <= 1 ? 'text' : 'list',
        text: items[0] || '',
        items,
        pairs: [],
      }
    },
    normalizeContentBlock(value, options = {}) {
      const preferPairs = options.preferPairs === true

      if (Array.isArray(value)) {
        const items = this.uniqueTrimmed(
          this.filterSectionHeaders(
            this.mergeFragmentedInitials(
              value.flatMap((entry) => {
                if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
                  return this.describeObjectItem(entry)
                }

                return this.splitStructuredText(entry)
              }),
            ),
          ),
        )

        return {
          hasContent: items.length > 0,
          type: items.length <= 1 ? 'text' : 'list',
          text: items[0] || '',
          items,
          pairs: [],
        }
      }

      if (value && typeof value === 'object') {
        const pairs = this.objectPairs(value)
        if (preferPairs && pairs.length) {
          return {
            hasContent: true,
            type: 'pairs',
            text: '',
            items: [],
            pairs,
          }
        }

        const items = pairs.map((pair) => `${pair.label}: ${pair.value}`)
        return {
          hasContent: items.length > 0,
          type: items.length <= 1 ? 'text' : 'list',
          text: items[0] || '',
          items,
          pairs,
        }
      }

      const items = this.splitStructuredText(value)
      return {
        hasContent: items.length > 0,
        type: items.length <= 1 ? 'text' : 'list',
        text: items[0] || '',
        items,
        pairs: [],
      }
    },
    objectPairs(value) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return []
      }

      return Object.entries(value)
        .map(([label, entry]) => ({
          label: this.humanizeLabel(label),
          value: Array.isArray(entry)
            ? this.uniqueTrimmed(entry.flatMap((item) => this.splitStructuredText(item))).join(', ')
            : entry && typeof entry === 'object'
              ? this.describeObjectItem(entry)
              : String(entry || '').replace(/\s+/g, ' ').trim(),
        }))
        .filter((item) => item.value)
    },
    describeObjectItem(value) {
      return this.objectPairs(value)
        .map((pair) => `${pair.label}: ${pair.value}`)
        .join(' | ')
    },
    humanizeLabel(value) {
      return String(value || '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^\w/, (char) => char.toUpperCase())
    },
    humanizeReviewStatus(value) {
      const map = {
        review_required: 'Review Required',
        ready_for_manager_use: 'Ready for Manager Use',
        blocked: 'Blocked',
      }
      const key = String(value || '').trim().toLowerCase()
      return map[key] || this.humanizeLabel(value)
    },
    extractReportFromDom() {
      const card = this.$refs.resultCard
      if (!card) return { html: '', text: '' }

      const title = String(this.resultHeaderTitle || '').trim()
      const meta = String(this.resultHeaderMeta || '').trim()

      const htmlParts = [`<h2>${this.escapeHtml(title)}</h2>`]
      const textParts = [`# ${title}`, '']
      if (meta) {
        htmlParts.push(`<p><em>${this.escapeHtml(meta)}</em></p>`)
        textParts.push(`_${meta}_`, '')
      }

      if (this.isRawMode) {
        // RawAskView renders a plain <pre> block with none of the section classes the
        // scraper below looks for, so pull the answer straight from state instead of the DOM.
        const rawAnswer = String(this.latestResponse?.answer || '').trim()
        if (rawAnswer) {
          htmlParts.push(`<pre>${this.escapeHtml(rawAnswer)}</pre>`)
          textParts.push(rawAnswer, '')
        }
        return {
          html: htmlParts.join(''),
          text: textParts.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
        }
      }

      const sectionSelectors = [
        '.snapshot-card__label',
        '.section-label',
        '.data-context-label',
      ].join(', ')

      const sectionHeadings = card.querySelectorAll(sectionSelectors)
      sectionHeadings.forEach((heading) => {
        const headingText = String(heading.textContent || '').trim()
        if (!headingText) return

        htmlParts.push(`<p><strong>${this.escapeHtml(headingText)}</strong></p>`)
        textParts.push(`**${headingText}**`, '')

        const container = heading.closest('.mode-card') || heading.parentElement
        if (!container) return

        // KAN-103 (LWD v2): a section of sub-lists (Follow-ups / Watch) or a plain table (Tools).
        const pushText = (el) => {
          const t = String(el?.textContent || '').trim()
          if (!t) return
          htmlParts.push(`<p>${this.escapeHtml(t)}</p>`)
          textParts.push(t, '')
        }
        const groups = container.querySelectorAll('.section-group')
        const plainTable = Array.from(container.querySelectorAll('table.section-table')).find((t) => !t.closest('.section-group'))
        const pushTable = (tableEl) => {
          const rows = Array.from(tableEl.querySelectorAll('tr'))
            .map((tr) => Array.from(tr.querySelectorAll('th, td')).map((cell) => String(cell.textContent || '').trim()))
            .filter((cells) => cells.length)
          htmlParts.push(`<table>${rows.map((cells) => `<tr>${cells.map((cell) => `<td>${this.escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}</table>`)
          rows.forEach((cells, i) => {
            textParts.push(`| ${cells.map((cell) => cell.replace(/\\/g, '\\\\').replace(/\|/g, '\\|')).join(' | ')} |`)
            if (i === 0) textParts.push(`|${cells.map(() => ' --- |').join('')}`)
          })
          textParts.push('')
        }
        if (groups.length || plainTable) {
          pushText(container.querySelector('.section-lead'))
          groups.forEach((group) => {
            const groupHeading = String(group.querySelector('.section-group__heading')?.textContent || '').trim()
            if (groupHeading) {
              htmlParts.push(`<p><strong>${this.escapeHtml(groupHeading)}</strong></p>`)
              textParts.push(`*${groupHeading}*`)
            }
            const items = Array.from(group.querySelectorAll('li')).map((li) => String(li.textContent || '').trim()).filter(Boolean)
            if (items.length) {
              htmlParts.push(`<ul>${items.map((item) => `<li>${this.escapeHtml(item)}</li>`).join('')}</ul>`)
              items.forEach((item) => textParts.push(`- ${item}`))
              textParts.push('')
            }
            group.querySelectorAll('.mode-text').forEach(pushText)
            // The rows behind "Show all" are copied too, open or not.
            group.querySelectorAll('table.section-table').forEach(pushTable)
          })
          if (plainTable) {
            pushTable(plainTable)
            pushText(container.querySelector('.section-group__more'))
          }
          return
        }

        const textEl = container.querySelector('.answer-text, .mode-text')
        if (textEl) {
          const t = String(textEl.textContent || '').trim()
          if (t) {
            htmlParts.push(`<p>${this.escapeHtml(t)}</p>`)
            textParts.push(t, '')
          }
        }

        const listEl = container.querySelector('.snapshot-list, .mode-list')
        if (listEl) {
          const items = Array.from(listEl.querySelectorAll('li'))
            .map((li) => String(li.textContent || '').trim())
            .filter(Boolean)
          if (items.length) {
            const isNumbered = listEl.classList.contains('snapshot-list--numbered') ||
              listEl.classList.contains('mode-list--inline-numbered')
            htmlParts.push(
              `<${isNumbered ? 'ol' : 'ul'}>${items.map((item) => `<li>${this.escapeHtml(item)}</li>`).join('')}</${isNumbered ? 'ol' : 'ul'}>`,
            )
            items.forEach((item, i) => textParts.push(isNumbered ? `${i + 1}. ${item}` : `- ${item}`))
            textParts.push('')
          }
        }

        // KAN-95: the daily Team table (TeamTableView) — rows as a table, plus its footer lines.
        const tableEl = container.querySelector('table.team-table')
        if (tableEl) {
          const rows = Array.from(tableEl.querySelectorAll('tr'))
            .map((tr) => Array.from(tr.querySelectorAll('th, td')).map((cell) => String(cell.textContent || '').replace(/[▲▼]/g, '').trim()))
            .filter((cells) => cells.length)
          if (rows.length) {
            htmlParts.push(
              `<table>${rows.map((cells) => `<tr>${cells.map((cell) => `<td>${this.escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}</table>`,
            )
            rows.forEach((cells, i) => {
              // Escape backslashes, then pipes, so a name containing `|` can't split a Markdown cell.
              textParts.push(`| ${cells.map((cell) => cell.replace(/\\/g, '\\\\').replace(/\|/g, '\\|')).join(' | ')} |`)
              if (i === 0) textParts.push(`|${cells.map(() => ' --- |').join('')}`)
            })
            textParts.push('')
          }
          container.querySelectorAll('.team-table-empty, .team-table-note, .team-table-scope').forEach((noteEl) => {
            const note = String(noteEl.textContent || '').trim()
            if (note) {
              htmlParts.push(`<p>${this.escapeHtml(note)}</p>`)
              textParts.push(note, '')
            }
          })
        }

        const pairsEl = container.querySelector('.dataset-context, .mode-pairs')
        if (pairsEl) {
          const dts = Array.from(pairsEl.querySelectorAll('dt'))
          dts.forEach((dt) => {
            const label = String(dt.textContent || '').trim()
            const dd = dt.nextElementSibling
            const value = dd ? String(dd.textContent || '').trim() : ''
            if (label && value) {
              htmlParts.push(`<p><strong>${this.escapeHtml(label)}:</strong> ${this.escapeHtml(value)}</p>`)
              textParts.push(`- **${label}:** ${value}`)
            }
          })
          if (dts.length) textParts.push('')
        }
      })

      return {
        html: htmlParts.join(''),
        text: textParts.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
      }
    },
    async copyReport() {
      const { html, text } = this.extractReportFromDom()
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ])
      } catch {
        await navigator.clipboard.writeText(text)
      }
    },
    exportReport(format) {
      if (format === 'md') {
        this.downloadReportAsMarkdown()
      } else if (format === 'pdf') {
        this.printReportAsPdf()
      } else if (format === 'pptx' || format === 'docx') {
        this.exportQbrReport(format)
      }
    },
    // See qbrReportClientName/qbrReportDateRange above for why these (not the live selection
    // state) are what's forwarded to the export endpoint.
    async exportQbrReport(format) {
      if (!this.qbrReportClientName || this.qbrExportingFormat) return
      this.qbrExportError = ''
      this.qbrExportingFormat = format
      try {
        const config = {
          headers: { Authorization: this.$cookies.get('teamwrkr-token') },
          responseType: 'blob',
          params: this.qbrReportDateRange?.startDate && this.qbrReportDateRange?.endDate
            ? { startDate: this.qbrReportDateRange.startDate, endDate: this.qbrReportDateRange.endDate }
            : undefined,
        }
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/qbr-reports/${encodeURIComponent(this.qbrReportClientName)}/latest/export/${format}`,
          config,
        )
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = this.extractExportFilename(response.headers['content-disposition']) || `qbr-export.${format}`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (error) {
        const status = error?.response?.status
        this.qbrExportError = status === 404
          ? 'No QBR report has been generated yet for this client.'
          : `Failed to export ${format.toUpperCase()} (${status || 'network error'}).`
      } finally {
        this.qbrExportingFormat = null
      }
    },
    extractExportFilename(contentDisposition) {
      const match = /filename="([^"]+)"/.exec(contentDisposition || '')
      return match ? match[1] : null
    },
    downloadReportAsMarkdown() {
      const { text } = this.extractReportFromDom()
      const slug = (this.resultHeaderTitle || 'report').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
      const blob = new Blob([text], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${slug}.md`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    printReportAsPdf() {
      const { html } = this.extractReportFromDom()
      const title = this.escapeHtml(this.resultHeaderTitle)
      const printWindow = window.open('', '_blank')
      if (!printWindow) return

      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; color: #1a202c; line-height: 1.6; }
    h2 { font-size: 1.4rem; margin: 0 0 0.5rem; }
    p { margin: 0.5rem 0; }
    ul, ol { margin: 0.25rem 0; padding-left: 1.5rem; }
    li + li { margin-top: 0.25rem; }
    strong { font-weight: 700; }
    em { font-style: italic; color: #4a5568; }
    pre { white-space: pre-wrap; word-break: break-word; font-family: inherit; font-size: 0.9rem; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>${html}</body>
</html>`)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    sanitizeAnswerHtml(rawHtml) {
      if (!rawHtml || typeof window === 'undefined') return ''

      const parser = new window.DOMParser()
      const doc = parser.parseFromString(`<div>${rawHtml}</div>`, 'text/html')
      const allowedTags = new Set(['p', 'ul', 'ol', 'li', 'strong', 'em', 'br'])

      const sanitizeNode = (node) => {
        if (node.nodeType === window.Node.TEXT_NODE) {
          return this.escapeHtml(node.textContent)
        }

        if (node.nodeType !== window.Node.ELEMENT_NODE) {
          return ''
        }

        const tagName = node.tagName.toLowerCase()

        if (!allowedTags.has(tagName)) {
          return Array.from(node.childNodes).map(sanitizeNode).join('')
        }

        if (tagName === 'br') {
          return '<br>'
        }

        const children = Array.from(node.childNodes).map(sanitizeNode).join('')
        return `<${tagName}>${children}</${tagName}>`
      }

      return Array.from(doc.body.childNodes).map(sanitizeNode).join('').trim()
    },
    extractPlainText(html) {
      if (!html || typeof window === 'undefined') return ''

      const parser = new window.DOMParser()
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
      return doc.body.textContent || ''
    },
    splitStructuredText(value) {
      if (Array.isArray(value)) {
        return value.flatMap((entry) => this.splitStructuredText(entry))
      }

      const text = String(value || '').trim()
      if (!text) return []

      const lines = text
        .split('\n')
        .map((line) => line.replace(/^\s*(?:[-*]|\d+\.)\s*/, '').trim())
        .filter(Boolean)

      if (lines.length > 1) {
        return this.uniqueTrimmed(lines)
      }

      return this.uniqueTrimmed(
        text
          .split(/(?:\s*\|\s*)|[.;]\s*-\s+(?=[A-Za-z])|(?<![A-Z])\.\s+(?=[A-Z])|[!?]\s+/)
          .map((item) => item.replace(/^\s*[-*]\s*/, '').trim()),
      )
    },
    normalizeMeta(data) {
      const responseMeta = data?.meta || {}
      const datasetContext = data?.datasetContext || {}
      const askMetadata = this.askMetadata || {}
      const askDateRange = askMetadata?.dateRange || {}
      const askIngestionJob = askMetadata?.ingestionJob || {}

      return {
        ...responseMeta,
        sourceLabel: this.pickFirst(
          responseMeta.sourceLabel,
          datasetContext.dataSource,
          datasetContext.sourceLabel,
          datasetContext.source,
          this.formatDataSource(askMetadata.dataSource),
        ),
        dateRangeLabel: this.pickFirst(
          responseMeta.dateRangeLabel,
          datasetContext.dateRangeLabel,
        ),
        dateRangeStart: this.pickFirst(
          responseMeta.dateRangeStart,
          datasetContext.dateRangeStart,
          askDateRange.start,
        ),
        dateRangeEnd: this.pickFirst(
          responseMeta.dateRangeEnd,
          datasetContext.dateRangeEnd,
          askDateRange.end,
        ),
        lastSyncedAt: this.pickFirst(
          responseMeta.lastSyncedAt,
          datasetContext.lastSyncedAt,
          askMetadata.lastSyncAt,
          askIngestionJob.completedAt,
        ),
        contextTicketCount: this.pickFirst(
          responseMeta.contextTicketCount,
          datasetContext.ticketCount,
          askMetadata.ticketCount,
        ),
        contextSnippetCount: this.pickFirst(
          responseMeta.contextSnippetCount,
          datasetContext.snippetCount,
        ),
        recordCount: this.pickFirst(
          responseMeta.recordCount,
          datasetContext.recordCount,
          datasetContext.volumeAnalyzed,
          askMetadata.ticketCount,
        ),
        dateRangeField: this.pickFirst(
          responseMeta.dateRangeField,
          datasetContext.dateRangeField,
          askDateRange.field,
        ),
        ingestionStatus: this.pickFirst(
          responseMeta.ingestionStatus,
          datasetContext.ingestionStatus,
          askIngestionJob.status,
        ),
        clientCount: this.pickFirst(
          responseMeta.clientCount,
          datasetContext.clientCount,
        ),
        employeeCount: this.pickFirst(
          responseMeta.employeeCount,
          datasetContext.employeeCount,
        ),
        provenanceLabel: this.pickFirst(
          responseMeta.provenanceLabel,
          datasetContext.provenanceLabel,
        ),
        isDemoData: datasetContext.isDemoData === true || responseMeta.isDemoData === true,
      }
    },
    formatCountLabel(value, singularLabel) {
      const count = Number(value)
      if (!Number.isFinite(count) || count <= 0) return ''
      return `${count} ${count === 1 ? singularLabel : `${singularLabel}s`}`
    },
    formatProvenance(provenanceLabel, isDemoData) {
      if (isDemoData === true) return 'Demo data'
      const normalized = String(provenanceLabel || '').trim().toLowerCase()
      if (normalized === 'production_like') return 'Production-like data'
      return normalized ? this.humanizeLabel(normalized) : ''
    },
    async fetchAskMetadata() {
      this.isMetadataLoading = true
      try {
        const config = {
          headers: { Authorization: this.$cookies.get('teamwrkr-token') },
        }
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/ask/meta-data`,
          config,
        )
        this.askMetadata = response?.data || null
      } catch (error) {
        this.askMetadata = null
      } finally {
        this.isMetadataLoading = false
      }
    },
    async fetchRosterOptions() {
      if (!Array.isArray(this.user?.roster) || !this.user.roster.length) return
      if (this.isRosterLoading) return
      if (Array.isArray(this.rosterMembers) && this.rosterMembers.length) return

      this.isRosterLoading = true
      try {
        const config = {
          headers: { Authorization: this.$cookies.get('teamwrkr-token') },
        }
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/roster`,
          config,
        )
        this.rosterMembers = Array.isArray(response?.data) ? response.data : []
      } catch (error) {
        this.rosterMembers = []
      } finally {
        this.isRosterLoading = false
      }
    },
    // Builds the display-ready message shape (loading/error/answer fields) from a raw
    // Conversation/Message row returned by Story 30.1's endpoints. `row.payload` holds the
    // full ask.service.js response object for assistant rows (null for user rows).
    buildConversationMessageFromRow(row) {
      if (!row) return null
      if (row.role === 'user') {
        return { role: 'user', question: row.text, displayQuestion: row.text }
      }
      const payload = row.payload || {}
      // Story 30.1 persists a failed turn as an assistant row with payload.error === true
      // (payload.message holding a safe summary) rather than no row at all, so a resumed
      // or reloaded thread can show the same error/retry state a live failure shows —
      // without this branch it would fall through to the normal answer shape below and
      // silently render as a blank "No response received." answer instead of an error.
      if (payload.error === true) {
        return {
          role: 'assistant',
          loading: false,
          answer: '',
          answerHtml: '',
          error: payload.message || 'This response failed to generate.',
          canRetry: true,
        }
      }
      return {
        role: 'assistant',
        loading: false,
        answer: payload.answer || row.text || 'No response received.',
        answerHtml: payload.answerHtml || '',
        raw: payload.raw === true,
        accessDenied: payload.accessDenied === true,
        signalStrength: payload.signalStrength || null,
        integrityCheck: payload.integrityCheck || null,
        responsePolicy: payload.responsePolicy || null,
        limitations: Array.isArray(payload.limitations) ? payload.limitations : [],
        // KAN-87: what a follow-up sends back about this answer (buildPriorTurns).
        ticketRefs: extractAnswerTicketRefs(payload),
        error: '',
        canRetry: false,
      }
    },
    // KAN-87: the last completed turns before the given assistant message (the one being asked
    // or retried), oldest first: question, plain-text answer and cited ticket numbers. Failed
    // and in-flight answers are skipped. conversationMessages is chronological (KAN-88 only
    // reverses the rendering).
    buildPriorTurns(beforeMessageId) {
      const stopIndex = beforeMessageId
        ? this.conversationMessages.findIndex((message) => message.id === beforeMessageId)
        : -1
      const messages = stopIndex >= 0 ? this.conversationMessages.slice(0, stopIndex) : this.conversationMessages
      const turns = []
      messages.forEach((message, index) => {
        if (message.role !== 'assistant' || message.loading || message.error) return
        const previous = messages[index - 1]
        const question = String(previous?.role === 'user' ? previous.question || '' : message.rawQuestion || '').trim()
        const answer = toPlainAnswerText(message.answer || message.answerHtml)
        if (!question || !answer) return
        turns.push({
          question: question.slice(0, PRIOR_TURN_QUESTION_CHARS),
          answer: answer.slice(0, PRIOR_TURN_ANSWER_CHARS),
          ticketRefs: Array.isArray(message.ticketRefs) ? message.ticketRefs.slice(0, PRIOR_TURN_TICKET_REFS) : [],
        })
      })
      return turns.slice(-PRIOR_TURNS_LIMIT)
    },
    replaceConversationMessage(id, patch) {
      this.conversationMessages = this.conversationMessages.map((message) => (
        message.id === id ? { ...message, ...patch } : message
      ))
    },
    // Shared by submitConversationMessage and retryConversationMessage: calls Story 30.1's
    // create-or-append endpoint (plain request/response, not SSE — per the user's streaming
    // scope decision for this story) and returns the assistant message to render.
    // Story 30.4: optional outputMode/dateRangePreset/workspaceSource let a quick-start
    // action's configured mode/date-range reach the backend instead of silently falling
    // back to conversation defaults (auto/all_time) — undefined fields are omitted so
    // regular freeform turns are unaffected.
    async requestConversationTurn(question, options = {}) {
      const token = this.$cookies.get('teamwrkr-token')
      const config = { headers: { Authorization: token } }
      const { outputMode, dateRangePreset, workspaceSource, beforeMessageId } = options
      // KAN-87: sent with every follow-up, saved thread or not (the server prefers its stored
      // turns when there is a conversation id); omitted only when there is no earlier turn.
      const priorTurns = this.buildPriorTurns(beforeMessageId)
      const body = {
        question,
        // Phil (2026-10-16): without this the server answered every freeform question at self
        // scope, so an admin on Company overview only ever saw their own tickets.
        scopeType: this.selectedScope,
        ...(outputMode !== undefined ? { outputMode } : {}),
        ...(dateRangePreset !== undefined ? { dateRangePreset } : {}),
        ...(workspaceSource !== undefined ? { workspaceSource } : {}),
        ...(priorTurns.length ? { priorTurns } : {}),
        ...(this.canUseRawMode && this.rawModeEnabled ? { rawMode: true } : {}),
      }
      const response = this.conversationId
        ? await this.axios.post(
            `${process.env.VUE_APP_API_URL}/ask/conversations/${this.conversationId}/messages`,
            body,
            config,
          )
        : await this.axios.post(
            `${process.env.VUE_APP_API_URL}/ask/conversations`,
            body,
            config,
          )

      // KAN-79: an ask made while a super user is impersonating comes back ephemeral
      // ({ id: null, ephemeral: true }) — nothing was saved, so there is no thread to continue
      // and nothing to show in History (which is only ever loaded from the server). Keep
      // conversationId null so the next turn starts fresh rather than appending to a saved thread.
      const responseConversation = response?.data?.conversation
      if (responseConversation?.ephemeral) {
        this.conversationId = null
      } else if (!this.conversationId) {
        this.conversationId = responseConversation?.id || null
      }

      const messages = Array.isArray(response?.data?.messages) ? response.data.messages : []
      const assistantRow = messages.find((row) => row.role === 'assistant')
      return this.buildConversationMessageFromRow(assistantRow) || {
        role: 'assistant',
        loading: false,
        answer: 'No response received.',
        answerHtml: '',
        error: '',
        canRetry: false,
      }
    },
    async submitConversationMessage(question, options = {}) {
      const trimmedQuestion = String(question || '').trim()
      if (!trimmedQuestion || this.isBusy) return

      this.conversationNotFound = false
      this.conversationOpenError = ''
      const requestDisplayQuestion = String(options.displayQuestion || trimmedQuestion).trim() || trimmedQuestion
      const assistantId = `conversation-msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

      this.conversationMessages = [
        ...this.conversationMessages,
        { id: `${assistantId}-question`, role: 'user', question: trimmedQuestion, displayQuestion: requestDisplayQuestion },
        {
          id: assistantId,
          role: 'assistant',
          rawQuestion: trimmedQuestion,
          displayQuestion: requestDisplayQuestion,
          // Carried so a later retry (Story 30.3) resubmits with the same ask options
          // (Story 30.4) rather than silently falling back to conversation defaults.
          outputMode: options.outputMode,
          dateRangePreset: options.dateRangePreset,
          workspaceSource: options.workspaceSource,
          loading: true,
          answer: '',
          answerHtml: '',
          error: '',
          canRetry: false,
        },
      ]

      this.isAsking = true
      this.scrollToNewestTurn({ highlight: true })

      let result = { failed: false, canRetry: false }
      try {
        const assistantMessage = await this.requestConversationTurn(trimmedQuestion, {
          outputMode: options.outputMode,
          dateRangePreset: options.dateRangePreset,
          workspaceSource: options.workspaceSource,
          beforeMessageId: `${assistantId}-question`,
        })
        this.replaceConversationMessage(assistantId, {
          ...assistantMessage,
          id: assistantId,
          rawQuestion: trimmedQuestion,
          displayQuestion: requestDisplayQuestion,
          outputMode: options.outputMode,
          dateRangePreset: options.dateRangePreset,
          workspaceSource: options.workspaceSource,
        })
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0)
        const errorMessage = error?.response?.data?.message || 'Unable to get a response right now.'
        result = { failed: true, canRetry: statusCode >= 500 }
        this.replaceConversationMessage(assistantId, {
          loading: false,
          error: errorMessage,
          canRetry: result.canRetry,
        })
      } finally {
        this.isAsking = false
        // KAN-88 (A7): bring the answer (or its error) into view once it lands, so it never
        // reads as the previous question being re-run.
        this.scrollToNewestTurn({ highlight: true })
      }
      return result
    },
    // KAN-88: scrolls the newest turn (rendered first) to the top of the viewport and, when
    // asked, highlights it for ~2s. Looked up by its data-turn-id rather than a conditional
    // :ref inside the v-for — Vue's ref array kept the previous top turn after a new turn was
    // prepended, so the scroll landed on the old question.
    scrollToNewestTurn({ highlight = false } = {}) {
      this.$nextTick(() => {
        const newestTurnId = this.conversationTurns[0]?.id || ''
        if (!newestTurnId) return
        const el = Array.from(this.$refs.resultCard?.querySelectorAll?.('[data-turn-id]') || [])
          .find((node) => node.getAttribute('data-turn-id') === newestTurnId)
        if (!el) return
        el.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
        if (!highlight) return
        clearTimeout(this.turnHighlightTimer)
        this.highlightedTurnId = newestTurnId
        this.turnHighlightTimer = setTimeout(() => {
          if (this.highlightedTurnId === newestTurnId) this.highlightedTurnId = ''
        }, TURN_HIGHLIGHT_MS)
      })
    },
    async retryConversationMessage(message) {
      if (!message || this.isBusy) return
      const trimmedQuestion = String(message.rawQuestion || message.question || '').trim()
      if (!trimmedQuestion) return

      this.replaceConversationMessage(message.id, { loading: true, error: '', canRetry: false })
      this.isAsking = true

      try {
        const assistantMessage = await this.requestConversationTurn(trimmedQuestion, {
          outputMode: message.outputMode,
          dateRangePreset: message.dateRangePreset,
          workspaceSource: message.workspaceSource,
          beforeMessageId: message.id,
        })
        this.replaceConversationMessage(message.id, {
          ...assistantMessage,
          id: message.id,
          rawQuestion: trimmedQuestion,
          displayQuestion: message.displayQuestion,
          outputMode: message.outputMode,
          dateRangePreset: message.dateRangePreset,
          workspaceSource: message.workspaceSource,
        })
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0)
        const errorMessage = error?.response?.data?.message || 'Unable to get a response right now.'
        this.replaceConversationMessage(message.id, {
          loading: false,
          error: errorMessage,
          canRetry: statusCode >= 500,
        })
      } finally {
        this.isAsking = false
      }
    },
    // Story 30.3: history panel toggle. Disabled while isBusy (AC5's "disable history
    // navigation while an answer is in flight" choice — simpler than making an in-flight
    // request resolve correctly against a hidden conversation).
    toggleHistoryPanel() {
      if (this.isBusy) return
      this.isHistoryPanelOpen = !this.isHistoryPanelOpen
      if (this.isHistoryPanelOpen) {
        this.loadConversationHistory()
      }
    },
    async loadConversationHistory() {
      this.isHistoryLoading = true
      this.historyError = ''
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/ask/conversations`,
          { headers: { Authorization: this.$cookies.get('teamwrkr-token') } },
        )
        this.conversationHistory = Array.isArray(response?.data?.conversations) ? response.data.conversations : []
      } catch (error) {
        this.conversationHistory = []
        this.historyError = 'Unable to load your conversations right now.'
      } finally {
        this.isHistoryLoading = false
      }
    },
    // Story 30.1's startConversation already sets a derived title (from the first question,
    // truncated) synchronously before returning, so title is non-null for every conversation
    // created through the real flow today. This fallback exists defensively per AC1, for any
    // row that somehow has no title, rather than because it's expected to trigger.
    conversationPreviewText(conversation) {
      const title = String(conversation?.title || '').trim()
      return title || 'Untitled conversation'
    },
    conversationTimestampText(conversation) {
      const raw = conversation?.updatedAt || conversation?.createdAt
      if (!raw) return ''
      const date = new Date(raw)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    },
    async openConversationFromHistory(conversation) {
      if (!conversation?.id || this.isBusy) return
      this.isHistoryPanelOpen = false
      this.conversationNotFound = false
      this.conversationOpenError = ''
      this.isAsking = true
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/ask/conversations/${conversation.id}`,
          { headers: { Authorization: this.$cookies.get('teamwrkr-token') } },
        )
        const payload = response?.data?.conversation
        const rows = Array.isArray(payload?.messages) ? payload.messages : []
        let precedingUserQuestion = ''
        this.conversationMessages = rows.map((row) => {
          const built = { ...this.buildConversationMessageFromRow(row), id: row.id }
          if (row.role === 'user') {
            precedingUserQuestion = row.text || ''
          } else if (built.error) {
            // Resumed error-state rows have no rawQuestion of their own (Story 30.1 only
            // persists the error, not the question that caused it) — without this, Retry
            // on a reopened conversation silently no-ops (retryConversationMessage bails
            // out when rawQuestion/question are both empty).
            built.rawQuestion = precedingUserQuestion
          }
          return built
        })
        this.conversationId = payload?.id || conversation.id
        this.latestResponse = null
        this.scrollToNewestTurn()
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0)
        if (statusCode === 404) {
          this.conversationNotFound = true
          this.conversationMessages = []
          this.conversationId = null
        } else {
          // Not historyError: the history panel is already closed (set above) before this
          // request even starts, so a message written there would never be visible. This
          // needs its own always-visible state, same placement as conversationNotFound.
          // Deliberately does not clear conversationMessages/conversationId — unlike the
          // 404 case (that conversation genuinely doesn't exist), a transient failure to
          // open a *different* historical conversation shouldn't blow away whatever thread
          // the user was already looking at.
          this.conversationOpenError = 'Unable to open that conversation right now.'
        }
      } finally {
        this.isAsking = false
      }
    },
    messageSanitizedAnswerHtml(message) {
      const answerHtml = String(message?.answerHtml || '').trim()
      if (!answerHtml) return ''
      return this.sanitizeAnswerHtml(answerHtml)
    },
    messageResponseParagraphs(message) {
      const sanitized = this.messageSanitizedAnswerHtml(message)
      const text = sanitized ? this.extractPlainText(sanitized) : String(message?.answer || '')
      const trimmed = String(text || '').trim()
      if (!trimmed) return []
      return trimmed
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
    },
    messageHasIntegrityBlock(message) {
      return message?.integrityCheck?.status === 'blocked' || message?.responsePolicy?.shouldBlock === true
    },
    messageHasStructuredFallback(message) {
      return Array.isArray(message?.limitations) && message.limitations.includes(STRUCTURED_FALLBACK_MESSAGE)
    },
    async submitQuestion(question, options = {}) {
      const trimmedQuestion = String(question || '').trim()
      if (!trimmedQuestion || this.isBusy) return

      const requestScope = this.selectedScope
      const requestDateRangePreset = options.dateRangePreset || this.selectedDateRangePreset
      const requestOutputMode = options.outputMode || 'auto'
      const requestWorkspaceSource = options.workspaceSource || null
      const requestAnalysisContext = options.analysisContext || null
      const requestRawMode = options.rawMode === true
      const requestRawDataOnly = options.rawDataOnly === true
      const requestDisplayQuestion = String(options.displayQuestion || trimmedQuestion).trim() || trimmedQuestion
      this.latestResponse = {
        question: requestDisplayQuestion,
        displayQuestion: requestDisplayQuestion,
        rawQuestion: trimmedQuestion,
        scopeType: requestScope,
        dateRangePreset: requestDateRangePreset,
        requestOutputMode,
        requestWorkspaceSource,
        requestAnalysisContext,
        loading: true,
        answer: '',
        answerHtml: '',
        outputMode: '',
        raw: false,
        accessDenied: false,
        signalStrength: null,
        queryIntent: null,
        responsePolicy: null,
        integrityCheck: null,
        evidenceProfile: null,
        decisionSupport: null,
        yesterdayReport: null,
        weeklyReport: null,
        toolActivity: null,
        lwdV2: null,
        departedCleanup: null,
        goneQuietCleanup: null,
        qbrBrief: null,
        evidenceReview: null,
        coachingBrief: null,
        reportView: null,
        citations: [],
        limitations: [],
        recommendations: [],
        meta: {},
        metaLine: '',
        error: '',
        canRetry: false,
      }

      this.isAsking = true
      this.ticketRefs = []
      this.drawerOpen = false
      this.drawerTicket = null
      this.streamingStatusMessage = ''
      this.streamingContextSummary = ''
      this.streamingContextMeta = {}
      this.startLoadingPhraseRotation()
      this.$nextTick(() => {
        this.$refs.resultCard?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })

      try {
        const token = this.$cookies.get('teamwrkr-token')
        const data = await this.streamAskResponse(
          trimmedQuestion,
          requestScope,
          token,
          requestDateRangePreset,
          requestOutputMode,
          requestWorkspaceSource,
          requestAnalysisContext,
          requestRawMode,
          requestRawDataOnly,
        )
        this.applyFinalAskPayload(data)
        // Scope to the specific auto-run action this page expects, not just "any successful
        // question" — submitQuestion is the shared entry point for auto-run, quick-start
        // actions, and freeform questions alike.
        if (requestWorkspaceSource?.actionId === this.autoRunActionId) {
          this.maybeOpenInitialTicketDrawer()
        }
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0)
        // If the server refuses executive (e.g. admin rights were removed since the page loaded),
        // drop back to the best remaining scope instead of retrying a scope that will keep 403ing.
        const executiveRefused = statusCode === 403 && requestScope === 'executive'
        if (executiveRefused) {
          this.selectedScope = this.resolvePreferredScope(
            this.availableScopeTypes.filter((scope) => scope !== 'executive'),
          )
        }
        const errorMessage = executiveRefused
          ? 'Company overview is only available to company admins.'
          : error?.response?.data?.message || 'Unable to get a response right now.'

        this.latestResponse = {
          ...this.latestResponse,
          loading: false,
          error: errorMessage,
          canRetry: statusCode >= 500,
        }
      } finally {
        this.closeAskStream()
        this.isAsking = false
        this.streamingStatusMessage = ''
        this.streamingContextSummary = ''
        this.streamingContextMeta = {}
        this.stopLoadingPhraseRotation()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ai-assistant {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: $space-5;
  margin-top: $space-6;
}

.ai-assistant__hero {
  background:
    radial-gradient(circle at top right, rgba(170, 118, 214, 0.14), transparent 30%),
    linear-gradient(180deg, rgba(244, 236, 251, 0.55), rgba(255, 255, 255, 0.96));
  border-radius: $radius-lg;
}

.ai-assistant--busy .ai-assistant__hero,
.ai-assistant--busy .ai-assistant__result {
  filter: grayscale(0.1);
}

.ai-assistant__header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: $space-4;
}

.hero-copy {
  max-width: 760px;
}

.ai-assistant__eyebrow,
.result-header__eyebrow,
.section-heading__eyebrow {
  margin: 0 0 $space-1;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-primary-dark;
}

.ai-assistant__title {
  margin: 0;
  font-size: clamp(1.7rem, 2vw, 2.4rem);
}

.ai-assistant__lede {
  margin: $space-2 0 0;
  color: $color-text-muted;
  max-width: 64ch;
}

.ai-assistant__context {
  display: block;
  margin-top: $space-5;
}

.context-summary {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: $space-4;
  margin-bottom: $space-3;
}

.context-summary__title {
  margin: 0;
  font-size: $font-size-lg;
  color: $color-text-strong;
}

.context-summary__helper {
  margin: 0;
  max-width: 360px;
  font-size: $font-size-sm;
  color: $color-text-muted;
  text-align: right;
}

.context-card {
  border: 1px solid rgba(170, 118, 214, 0.18);
  border-radius: $radius-md;
  background: rgba(255, 255, 255, 0.88);
  padding: $space-3;
}

.context-card--summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-4;
}

.context-card__row {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.context-card__row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
}

.context-card__label,
.snapshot-card__label {
  margin: 0 0 $space-1;
  font-size: $font-size-xs;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $color-text-strong;
  font-weight: 800;
}

.context-card__value {
  margin: 0;
  font-size: $font-size-md;
  font-weight: 700;
  color: $color-text-strong;
}

.context-selection-summary {
  margin: $space-3 0 0;
  font-size: $font-size-sm;
  color: $color-text-muted;
}

.workspace-quick-start {
  margin-top: $space-5;
  padding-top: $space-4;
  border-top: 1px solid rgba(170, 118, 214, 0.16);
}

.workspace-guided-actions {
  display: flex;
  flex-direction: column;
  gap: $space-4;
  margin-top: $space-5;
  padding-top: $space-4;
  border-top: 1px solid rgba(170, 118, 214, 0.16);
}

.workspace-guided-actions__group {
  display: flex;
  flex-direction: column;
  gap: $space-3;
}

.workspace-section {
  display: flex;
  flex-direction: column;
  gap: $space-4;
  padding: $space-4;
}

.workspace-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
}

.workspace-section__title {
  margin: 0;
  font-size: $font-size-lg;
  color: $color-text-strong;
}

.workspace-action-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: $space-3;
}

.workspace-area-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: $space-3;
}

.workspace-action-entry {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.workspace-action-btn,
.workspace-area-card,
.workspace-subprompt-btn {
  appearance: none;
  width: 100%;
  border: 1px solid rgba(146, 92, 202, 0.25);
  border-radius: $radius-md;
  background: rgba(255, 255, 255, 0.9);
  color: $color-text-strong;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  box-shadow: 0 6px 18px rgba(91, 39, 143, 0.04);
}

.workspace-action-btn:hover,
.workspace-area-card:hover,
.workspace-subprompt-btn:hover {
  border-color: rgba(146, 92, 202, 0.5);
  box-shadow: 0 10px 24px rgba(91, 39, 143, 0.08);
  transform: translateY(-1px);
}

.workspace-action-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: 72px;
  padding: $space-3 $space-4;
  text-align: left;
}

.workspace-action-btn__label {
  font-weight: 700;
}

.beta-badge {
  display: inline-flex;
  align-items: center;
  margin-left: $space-1;
  padding: 0.05rem 0.5rem;
  border-radius: $radius-pill;
  background: $color-primary-soft;
  color: $color-primary-dark;
  font-size: $font-size-xs;
  font-weight: 700;
  vertical-align: middle;
}

.workspace-action-btn__helper,
.workspace-subprompt-btn__helper {
  margin-top: $space-1;
  font-size: $font-size-sm;
  font-weight: 500;
  color: $color-text-muted;
}

.workspace-action-entry__context {
  align-self: flex-start;
  padding: 0;
  font-size: $font-size-sm;
}

.workspace-area-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
  padding: $space-4;
  text-align: left;
}

.workspace-area-card--active {
  border-color: rgba(146, 92, 202, 0.65);
  box-shadow: 0 12px 30px rgba(91, 39, 143, 0.1);
}

.workspace-area-card__body {
  display: flex;
  align-items: flex-start;
  gap: $space-3;
}

.workspace-area-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(146, 92, 202, 0.08);
  font-size: 1.1rem;
}

.workspace-area-card__title,
.workspace-subprompt-panel__title {
  margin: 0;
  font-size: $font-size-md;
  color: $color-text-strong;
  line-height: 1.3;
}

.workspace-area-card__description {
  margin: $space-1 0 0;
  color: $color-text-muted;
  font-size: $font-size-sm;
  line-height: 1.45;
}

.workspace-area-card__chevron {
  font-size: 1.5rem;
  color: $color-primary;
}

.workspace-subprompt-panel {
  border-top: 1px solid rgba(170, 118, 214, 0.16);
  padding-top: $space-4;
}

.workspace-subprompt-panel__header {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  margin-bottom: $space-3;
}

.workspace-subprompt-panel__description,
.workspace-section__copy {
  margin: 0;
  color: $color-text-muted;
  font-size: $font-size-sm;
  line-height: 1.5;
}

.workspace-subprompt-panel__group {
  display: flex;
  flex-direction: column;
  gap: $space-3;
}

.workspace-subprompt-panel__group + .workspace-subprompt-panel__group {
  margin-top: $space-4;
}

.workspace-subprompt-panel__group-label {
  margin: 0;
  font-size: $font-size-sm;
  font-weight: 700;
  color: $color-text-strong;
}

.workspace-subprompt-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
}

.workspace-subprompt-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: 48px;
  padding: $space-3;
  text-align: left;
  font-weight: 600;
}

.workspace-subprompt-btn__label {
  font-weight: 700;
}

.workspace-modal-card {
  width: min(860px, calc(100vw - #{$space-5 * 2}));
  max-width: 100%;
  max-height: calc(100vh - #{$space-4 * 2});
  overflow: hidden;
  border-radius: $radius-lg;
  background: linear-gradient(180deg, rgba(252, 247, 255, 0.98), rgba(255, 255, 255, 1));
  box-shadow: 0 28px 64px rgba(15, 23, 42, 0.28);
}

.workspace-modal__body {
  display: flex;
  flex-direction: column;
  gap: $space-4;
  overflow-y: auto;
  padding: $space-5;
}

.workspace-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $space-3;
  padding: $space-4 $space-5 $space-3;
  border-bottom: 1px solid rgba(146, 92, 202, 0.16);
  background: transparent;
  max-height: 40vh;
  overflow-y: auto;
}

.workspace-modal__title {
  margin: 0;
  font-size: $font-size-lg;
  color: $color-text-strong;
}

.workspace-modal__helper {
  margin: $space-1 0 0;
  color: $color-text-muted;
  font-size: $font-size-sm;
  line-height: 1.5;
}

.workspace-modal__helper--secondary {
  max-width: 62ch;
}

.workspace-modal__helper--question {
  white-space: pre-wrap;
  word-break: break-word;
}

.workspace-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-4;
}

.workspace-modal__field {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.workspace-modal__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
}

.workspace-modal__label {
  font-size: $font-size-sm;
  font-weight: 700;
  color: $color-text-strong;
}

.workspace-modal__date-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
}

.workspace-modal__picker {
  display: grid;
  gap: $space-2;
  max-height: 16rem;
  overflow-y: auto;
  padding: $space-3;
  border: 1px solid rgba(146, 92, 202, 0.18);
  border-radius: $radius-md;
  background: rgba(255, 255, 255, 0.88);
}

.workspace-modal__check {
  display: flex;
  align-items: center;
  gap: $space-2;
  color: $color-text;
  font-size: $font-size-sm;
}

.workspace-modal__check input {
  accent-color: $color-primary;
}

.workspace-modal__hint {
  margin: 0;
  color: $color-text-muted;
  font-size: $font-size-sm;
}

.workspace-modal__quarter-label {
  margin: 0;
  font-size: $font-size-sm;
  font-weight: 600;
  color: $color-text-strong;
}

.workspace-modal__client-select {
  width: 100%;
  padding: $space-2 $space-3;
  border: 1px solid $color-border;
  border-radius: $radius-sm;
  background: $color-surface;
  font-size: $font-size-sm;
  color: $color-text;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.workspace-modal__actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: $space-3;
  padding: $space-3 $space-5 $space-4;
  border-top: 1px solid rgba(146, 92, 202, 0.16);
  background: rgba(255, 255, 255, 0.94);
}

.ai-assistant__composer {
  margin-top: $space-5;
}

.ai-assistant__composer--workspace {
  margin-top: 0;
}

.composer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $space-3;
}

.composer-footer__actions {
  display: flex;
  align-items: center;
  gap: $space-2;
}

.char-count {
  color: $color-text-subtle;
  font-size: $font-size-sm;
}

.composer-footer__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $space-3;
}

.raw-mode-toggle {
  font-size: $font-size-sm;
}

.raw-mode-toggle__hint {
  margin-left: $space-1;
  color: $color-text-subtle;
}

.link-btn {
  border: 0;
  background: transparent;
  color: $color-primary-dark;
  font: inherit;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
}

.composer-footer__new-conversation:disabled {
  color: $color-text-subtle;
  cursor: not-allowed;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $space-3;
  margin-bottom: $space-4;
}

.result-header__title {
  margin: 0;
  line-height: 1.3;
}

.result-header__meta {
  margin: 0;
  color: $color-text-subtle;
  font-size: $font-size-sm;
  text-align: right;
}

.result-header__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: $space-2;
  flex-shrink: 0;
}

.result-header__actions {
  display: flex;
  gap: $space-2;
  align-items: center;
}

.result-action-btn {
  background: $color-surface;
  border: 1px solid $color-border;
  border-radius: $radius-sm;
  padding: $space-1 $space-2;
  font: inherit;
  font-size: $font-size-sm;
  color: $color-text-muted;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: $color-surface-muted;
  }
}

.result-action-error {
  font-size: $font-size-xs;
  color: $color-danger;
}

.thread-item {
  display: flex;
  flex-direction: column;
  gap: $space-3;
  border-radius: $radius-md;
  // Keeps the top of the turn clear of the viewport edge when scrolled to with block: 'start'.
  scroll-margin-top: $space-5;
  transition: background-color 0.4s ease, box-shadow 0.4s ease;
}

// KAN-88: space between turns, so each answer sits with its own question (and the highlight
// ring has room).
.thread-item + .thread-item {
  margin-top: $space-5;
}

// KAN-88: brief highlight on the turn just asked / answered (cleared after ~2s).
.thread-item--new {
  background: $color-highlight;
  box-shadow: 0 0 0 $space-2 $color-highlight;
}

.conversation-history-panel {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
  margin-bottom: $space-3;
}

.conversation-history-list {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  margin: 0;
  padding: 0;
  list-style: none;
}

.conversation-history-item__btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $space-3;
  width: 100%;
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-2 $space-3;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.conversation-history-item__title {
  font-size: $font-size-sm;
}

.conversation-history-item__meta {
  color: $color-text-subtle;
  font-size: $font-size-xs;
  white-space: nowrap;
}

.question-bubble {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;

  p {
    margin: 0;
  }
}

.bubble-label {
  color: $color-text-subtle;
  font-size: $font-size-xs;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: $space-1 !important;
}

.answer-card {
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.95), rgba(255, 255, 255, 1));
  padding: $space-5;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.04);
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.answer-snapshot,
.answer-signals,
.answer-evidence,
.answer-narrative {
  margin-bottom: $space-4;
}

.section-heading {
  margin-bottom: $space-3;
}

.section-heading h4 {
  margin: 0;
  font-size: $font-size-lg;
}

.snapshot-section {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
}

.snapshot-section__block + .snapshot-section__block {
  margin-top: $space-4;
  padding-top: $space-4;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.snapshot-card__value {
  margin: 0;
  font-weight: 600;
}

.snapshot-list {
  margin: 0;
  padding-left: 1.2rem;
}

.snapshot-list--bulleted {
  list-style: disc;
}

.snapshot-list li + li {
  margin-top: $space-2;
}

.snapshot-list--numbered {
  padding-left: 1.35rem;
}

.evidence-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
}

.signals-list {
  margin: 0;
  padding-left: 1.2rem;
}

.signals-list li + li {
  margin-top: $space-2;
}

.answer-text,
.meta-line {
  margin: 0;
}

.loading-skeleton {
  border-radius: $radius-md;
  background: linear-gradient(
    90deg,
    rgba(240, 241, 245, 0.85) 25%,
    rgba(226, 230, 238, 0.95) 50%,
    rgba(240, 241, 245, 0.85) 75%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.loading-skeleton--summary {
  height: 7rem;
}

.loading-skeleton--line {
  height: 1rem;
}

.loading-skeleton--line-short {
  width: 68%;
  height: 1rem;
}

.answer-text {
  white-space: pre-line;
}

.answer-text + .answer-text {
  margin-top: $space-3;
}

.answer-html :deep(p) {
  margin: 0;
}

.answer-html :deep(p + p),
.answer-html :deep(p + ul),
.answer-html :deep(p + ol),
.answer-html :deep(ul + p),
.answer-html :deep(ol + p),
.answer-html :deep(ul + ul),
.answer-html :deep(ol + ol),
.answer-html :deep(ul + ol),
.answer-html :deep(ol + ul) {
  margin-top: $space-3;
}

.answer-html :deep(ul),
.answer-html :deep(ol) {
  margin: 0;
  padding-left: 1.35rem;
}

.answer-html :deep(ul) {
  list-style: disc;
}

.answer-html :deep(ol) {
  list-style: decimal;
}

.answer-html :deep(li + li) {
  margin-top: $space-2;
}

.meta-line {
  margin-top: $space-3;
  color: $color-text-subtle;
  font-size: $font-size-sm;
}

.evidence-details {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  margin-top: $space-3;
}

.fallback-output,
.qbr-layout {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.fallback-banner {
  border: 1px solid rgba(180, 83, 9, 0.25);
  border-radius: $radius-md;
  background: rgba(255, 247, 237, 0.9);
  padding: $space-3;
}

.fallback-banner__title,
.fallback-banner__text,
.qbr-layout__objective,
.qbr-layout__title {
  margin: 0;
}

.fallback-banner__title {
  font-weight: 700;
  color: #9a3412;
}

.fallback-banner__text {
  margin-top: $space-1;
  color: #7c2d12;
}

.qbr-layout__header {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.qbr-layout__title {
  font-size: $font-size-lg;
}

.qbr-layout__objective {
  color: $color-text-muted;
}

.qbr-layout__section {
  margin-bottom: 0;
}

.qbr-layout__pairs,
.dataset-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: 0;
}

.qbr-layout__pair,
.dataset-context__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.qbr-layout__pair dt,
.dataset-context__item dt {
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $color-text-subtle;
}

.qbr-layout__pair dd,
.dataset-context__item dd {
  margin: 0;
  color: $color-text-strong;
}

.decision-support {
  display: flex;
  flex-direction: column;
  gap: $space-3;
}

.decision-support__grid {
  display: grid;
  gap: $space-3;
}

.decision-support__block {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.decision-support__block--priority {
  background: rgba(15, 118, 110, 0.06);
}

.decision-support__priority,
.decision-support__entities {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.decision-support__priority-item,
.decision-support__entity-group {
  margin: 0;
}

.answer-details summary {
  font-weight: 600;
  color: $color-primary-dark;
  cursor: pointer;
}

.answer-details ul {
  margin: $space-2 0 0;
  padding-left: 1.1rem;
}

.answer-narrative {
  border-top: 1px solid $color-border;
  padding-top: $space-3;
}

.answer-narrative--standalone {
  border-top: 0;
  padding-top: 0;
  margin-bottom: 0;
}

.answer-narrative summary {
  font-weight: 600;
  color: $color-primary-dark;
  cursor: pointer;
}

.answer-narrative[open] summary {
  margin-bottom: $space-3;
}

.ai-assistant__overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-5;
  background: rgba(248, 250, 252, 0.72);
  backdrop-filter: blur(3px);
}

.overlay-card {
  width: min(560px, 100%);
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  box-shadow: $shadow-md;
  padding: $space-5;
  text-align: center;
}

.overlay-title {
  margin: 0 0 $space-2;
  font-weight: 700;
}

.overlay-phrase {
  margin: 0;
  color: $color-text-muted;
}

.overlay-dots {
  display: inline-block;
  width: 3ch;
  text-align: left;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 1100px) {
  .workspace-action-grid,
  .workspace-area-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .qbr-layout__pairs,
  .dataset-context {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .evidence-grid {
    grid-template-columns: 1fr;
  }

  .context-card--summary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .workspace-section,
  .answer-card {
    padding: $space-3;
  }

  .workspace-action-grid,
  .workspace-area-grid,
  .workspace-subprompt-grid {
    grid-template-columns: 1fr;
  }

  .workspace-modal__grid,
  .workspace-modal__date-grid {
    grid-template-columns: 1fr;
  }

  .workspace-modal__body,
  .workspace-modal__header,
  .workspace-modal__actions {
    padding-left: $space-4;
    padding-right: $space-4;
  }

  .ai-assistant__header,
  .result-header {
    flex-direction: column;
    align-items: stretch;
  }

  .result-header__right {
    align-items: flex-start;
  }

  .result-header__meta {
    text-align: left;
  }
}

@media (max-width: 600px) {
  .composer-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .context-card__row-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
